import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../utils/api';
import EmailConfirmationModal from '../components/EmailConfirmationModal';
import EmailPreviewEditModal from '../components/EmailPreviewEditModal';

const formatDueDate = (task) => {
  if (!task.dueDate) return null;
  const now = new Date();
  if (task.isRepeated === 'true') {
    if (task.cycle === 'monthly') {
      const day = parseInt(task.dueDate, 10);
      if (isNaN(day)) return `Due: ${task.dueDate}`;
      // Clamp to last day of current month if needed
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const actualDay = Math.min(day, lastDay);
      const dueDate = new Date(now.getFullYear(), now.getMonth(), actualDay);
      return `Due: ${dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (task.cycle === 'yearly') {
      const parts = task.dueDate.split('-');
      if (parts.length === 2) {
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        if (month >= 1 && month <= 12 && !isNaN(day)) {
          const lastDay = new Date(now.getFullYear(), month, 0).getDate();
          const actualDay = Math.min(day, lastDay);
          const dueDate = new Date(now.getFullYear(), month - 1, actualDay);
          return `Due: ${dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
      }
      return `Due: ${task.dueDate}`;
    }
  }
  return `Due: ${new Date(task.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const AdminTodolist = ({ userInfo }) => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [adminTasks, setAdminTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [taskFilter, setTaskFilter] = useState('all'); // all, pending, completed
  const [bankInfo, setBankInfo] = useState([]);
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [newCompanyEmail, setNewCompanyEmail] = useState('');
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [expandedGmailTask, setExpandedGmailTask] = useState(null);
  const [companyDomain, setCompanyDomain] = useState('');
  const [monthlyTasksExpanded, setMonthlyTasksExpanded] = useState(true);
  const [monthlyTaskStatus, setMonthlyTaskStatus] = useState({});
  const [completedTasksExpanded, setCompletedTasksExpanded] = useState(false);
  const [pmsTransfersConfirmed, setPmsTransfersConfirmed] = useState({});
  const [monthlyTransactionStatus, setMonthlyTransactionStatus] = useState({});

  // Monthly cycle settings state
  const [showMonthlyCycleModal, setShowMonthlyCycleModal] = useState(false);
  const [monthlyCycleSettings, setMonthlyCycleSettings] = useState({
    cycleStartMonth: null,
    isPaused: false
  });
  const [selectedStartYear, setSelectedStartYear] = useState(new Date().getFullYear());
  const [selectedStartMonth, setSelectedStartMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [savingCycleSettings, setSavingCycleSettings] = useState(false);

  // Month names for selection
  const monthNames = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Get available years
  const getAvailableYears = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2];
  };

  const availableYears = getAvailableYears();

  // Generate list of months from cycle start until now (returns empty if no cycle configured)
  const getMonthlyTasks = (cycleStartMonth) => {
    // If no cycle start month is set, return empty array (no tasks)
    if (!cycleStartMonth) {
      return [];
    }

    const months = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Use cycle start month
    const [yearStr, monthStr] = cycleStartMonth.split('-');
    const startYear = parseInt(yearStr);
    const startMonthIndex = parseInt(monthStr) - 1; // Convert to 0-indexed

    // Generate months from start until current month
    let year = startYear;
    let monthIndex = startMonthIndex;

    while (year < currentYear || (year === currentYear && monthIndex <= currentMonth)) {
      const monthName = new Date(year, monthIndex, 1).toLocaleString('default', { month: 'long' });
      months.push({
        label: `${monthName} ${year}`,
        value: `${year}-${(monthIndex + 1).toString().padStart(2, '0')}`
      });

      monthIndex++;
      if (monthIndex > 11) {
        monthIndex = 0;
        year++;
      }
    }

    // Reverse to show most recent first
    return months.reverse();
  };

  const monthlyTasks = getMonthlyTasks(monthlyCycleSettings.cycleStartMonth);
  const [bulkEmailUpdates, setBulkEmailUpdates] = useState({});

  const getSubmissionFounders = (submission) => {
    if (!submission) return [];

    const foundersData = submission['Founders JSON'] ?? submission.founders ?? [];

    try {
      const parsedFounders = typeof foundersData === 'string'
        ? JSON.parse(foundersData)
        : foundersData;

      return Array.isArray(parsedFounders) ? parsedFounders : [];
    } catch (e) {
      console.error('Error parsing founders:', e);
      return [];
    }
  };

  const getSubmissionFounderEmails = (submission) => {
    return getSubmissionFounders(submission)
      .map(founder => founder?.email)
      .filter(Boolean)
      .join(', ');
  };

  // Email confirmation modal state
  const [emailConfirmation, setEmailConfirmation] = useState({
    isOpen: false,
    emailData: null,
    onConfirm: null
  });

  // EIN email preview modal state
  const [showEINEmailPreview, setShowEINEmailPreview] = useState(false);
  const [einTaskId, setEinTaskId] = useState(null);
  const [einEmailVariables, setEinEmailVariables] = useState({});
  const [sendingEINEmail, setSendingEINEmail] = useState(false);

  // Gmail bulk email preview modal state
  const [showGmailBulkEmailPreview, setShowGmailBulkEmailPreview] = useState(false);
  const [gmailBulkEmailData, setGmailBulkEmailData] = useState({});
  const [sendingGmailBulkEmail, setSendingGmailBulkEmail] = useState(false);

  // Gmail original email modal state
  const [showGmailOriginalEmailPreview, setShowGmailOriginalEmailPreview] = useState(false);
  const [gmailOriginalEmailData, setGmailOriginalEmailData] = useState({});
  const [sendingGmailOriginalEmail, setSendingGmailOriginalEmail] = useState(false);

  // UEI form state
  const [ueiFormData, setUeiFormData] = useState({});
  const [showBoardMinutesEmailPreview, setShowBoardMinutesEmailPreview] = useState(false);
  const [boardMinutesEmailData, setBoardMinutesEmailData] = useState({});
  const [boardMinutesTaskId, setBoardMinutesTaskId] = useState(null);
  const [sendingBoardMinutesEmail, setSendingBoardMinutesEmail] = useState(false);

  // CAGE email preview modal state
  const [showCAGEEmailPreview, setShowCAGEEmailPreview] = useState(false);
  const [cageTaskId, setCageTaskId] = useState(null);
  const [cageEmailVariables, setCageEmailVariables] = useState({});
  const [sendingCAGEEmail, setSendingCAGEEmail] = useState(false);

  // eRA email preview modal state
  const [showERAEmailPreview, setShowERAEmailPreview] = useState(false);
  const [eraTaskId, setEraTaskId] = useState(null);
  const [eraEmailVariables, setEraEmailVariables] = useState({});
  const [sendingERAEmail, setSendingERAEmail] = useState(false);

  // SO/FSR Confirmation email preview modal state (for SBA registration notification)
  const [showSBANotificationPreview, setShowSBANotificationPreview] = useState(false);
  const [soFsrTaskId, setSoFsrTaskId] = useState(null);
  const [sbaEmailVariables, setSbaEmailVariables] = useState({});
  const [sendingSBANotification, setSendingSBANotification] = useState(false);

  // Grants.gov AOR notification email preview modal state
  const [showAORNotificationPreview, setShowAORNotificationPreview] = useState(false);
  const [aorNotificationTaskId, setAorNotificationTaskId] = useState(null);
  const [aorEmailVariables, setAorEmailVariables] = useState({});
  const [sendingAORNotification, setSendingAORNotification] = useState(false);

  // AOR Confirmation completion email preview modal state
  const [showAORConfirmationEmailPreview, setShowAORConfirmationEmailPreview] = useState(false);
  const [aorConfirmTaskId, setAorConfirmTaskId] = useState(null);
  const [aorConfirmEmailVariables, setAorConfirmEmailVariables] = useState({});
  const [sendingAORConfirmationEmail, setSendingAORConfirmationEmail] = useState(false);

  // Task completion inputs state
  const [taskInputs, setTaskInputs] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});

  // SO/AOR state
  const [companyData, setCompanyData] = useState(null);
  const [admins, setAdmins] = useState([]);

  // Custom admin tasks state
  const [customTasks, setCustomTasks] = useState([]);
  const [customTaskCompletions, setCustomTaskCompletions] = useState([]);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [customTasksExpanded, setCustomTasksExpanded] = useState(true);
  // Bumps to force a re-render when the per-company "onboarding setup complete"
  // flag in localStorage changes (used by the skip-onboarding setup task panel).
  // eslint-disable-next-line no-unused-vars
  const [onboardingSetupTick, setOnboardingSetupTick] = useState(0);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    isRepeated: false,
    repeatDate: '',
    cycle: 'monthly',
    assignedTo: 'admin',
    taskType: 'mark-as-complete',
    fileUploadItems: [],
  });
  const [newFileUploadItem, setNewFileUploadItem] = useState('');

  useEffect(() => {
    if (userInfo) {
      fetchAdminData();
      fetchAdmins();
      fetchMonthlyTaskStatus();
      fetchMonthlyCycleSettings();
      fetchCustomTasks();
    }
  }, [userInfo]);

  // Fetch PMS and transaction status when monthlyTasks changes (after cycle settings loaded)
  useEffect(() => {
    if (monthlyTasks.length > 0) {
      fetchPmsAndTransactionStatus();
    }
  }, [monthlyTasks.length, monthlyCycleSettings.cycleStartMonth]);

  // Fetch company users and company data when a submission is selected
  useEffect(() => {
    if (selectedSubmission && Array.isArray(adminTasks.tasks) && adminTasks.tasks.length > 0) {
      // Find any task with a companyName to fetch company data
      const taskWithCompany = adminTasks.tasks.find(t => t.companyName);

      if (taskWithCompany && taskWithCompany.companyName) {
        fetchCompanyUsers(taskWithCompany.companyName);
        fetchCompanyData(taskWithCompany.companyName);
      }
    }
  }, [selectedSubmission, adminTasks]);

  // Poll for COF uploads and bank info submissions every 30 seconds
  useEffect(() => {
    if (userInfo && selectedSubmission) {
      const pollUpdates = async () => {
        try {
          // Fetch latest admin tasks to check for COF uploads and bank info
          const tasksResponse = await api.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/tasks`
          );

          if (tasksResponse.ok) {
            const tasksData = await tasksResponse.json();
            setAdminTasks(tasksData);
          }

          // Refresh bank info as well
          const bankInfoResponse = await api.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/bank-info`
          );

          if (bankInfoResponse.ok) {
            const bankInfoData = await bankInfoResponse.json();
            setBankInfo(bankInfoData.bankInfo || []);
          }
        } catch (error) {
          console.error('Error polling for updates:', error);
        }
      };

      // Poll every 30 seconds
      const interval = setInterval(pollUpdates, 30000);
      return () => clearInterval(interval);
    }
  }, [userInfo, selectedSubmission]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      // Fetch all company formation submissions
      const submissionsResponse = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/submissions`
      );
      
      let submissionsData = [];
      let tasksData = {};
      if (submissionsResponse.ok) {
        submissionsData = await submissionsResponse.json();
        setSubmissions(submissionsData);
        // Auto-select the single submission (each company has one)
        if (submissionsData && submissionsData.length > 0) {
          setSelectedSubmission(submissionsData[0]);
        }
      }

      // Fetch all admin tasks
      const tasksResponse = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/tasks`
      );

      if (tasksResponse.ok) {
        tasksData = await tasksResponse.json();
        console.log("fetchAdminData - All Tasks Data: ", tasksData);

        // Log EIN task specifically to see if einNumber is present
        const allTasks = tasksData.tasks || tasksData;
        if (Array.isArray(allTasks)) {
          const einTask = allTasks.find(t => t.taskName === 'Apply for EIN');
          console.log("fetchAdminData - EIN Task found:", einTask);
          if (einTask) {
            console.log("fetchAdminData - EIN Task einNumber:", einTask.einNumber);
          }
        }

        setAdminTasks(tasksData);
      }

      // Fetch bank information
      try {
        const bankInfoResponse = await api.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/bank-info`
        );

        if (bankInfoResponse.ok) {
          const bankInfoData = await bankInfoResponse.json();
          console.log('Admin bank info data:', bankInfoData);
          setBankInfo(bankInfoData.bankInfo || []);
        } else {
          console.error('Failed to fetch bank info:', bankInfoResponse.status);
          setBankInfo([]); // Set empty array on failure
        }
      } catch (bankInfoError) {
        console.error('Error fetching bank info:', bankInfoError);
        setBankInfo([]); // Set empty array on error
      }

      // Always fetch company data directly (needed for skipOnboarding check)
      // Admins don't have companyName on userInfo, so derive it from admin tasks or submissions
      try {
        const companiesResponse = await api.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/auth/companies`
        );
        if (companiesResponse.ok) {
          const companies = await companiesResponse.json();
          // Try multiple sources to find the company name
          // (use local variables since React state hasn't updated yet)
          let companyNameToFind = userInfo?.companyName || userInfo?.selectedCompany;
          if (!companyNameToFind) {
            // Try from admin tasks (local var from this fetch)
            const localTasks = tasksData?.tasks || tasksData || [];
            if (Array.isArray(localTasks)) {
              const taskWithCompany = localTasks.find(t => t.companyName);
              if (taskWithCompany) companyNameToFind = taskWithCompany.companyName;
            }
          }
          if (!companyNameToFind) {
            // Try from submissions (local var from this fetch)
            if (submissionsData.length > 0) {
              companyNameToFind = submissionsData[0]['Company Name'] || submissionsData[0].companyName;
            }
          }
          if (!companyNameToFind && companies.length === 1) {
            // If there's only one company, use it
            companyNameToFind = companies[0].name;
          }
          if (companyNameToFind) {
            const userCompany = companies.find(c => c.name === companyNameToFind);
            if (userCompany) {
              setCompanyData(userCompany);
            }
          }
        }
      } catch (companyError) {
        console.error('Error fetching company data:', companyError);
      }

    } catch (error) {
      console.error('Error fetching admin data:', error);
      setError('Failed to load admin data');
    }
    setLoading(false);
  };

  const fetchMonthlyTaskStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/month-status/month-completion-status`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Fetched monthly task status:", data);
        const statusMap = {};

        if (data.months) {
          Object.entries(data.months).forEach(([monthYear, status]) => {
            statusMap[monthYear] = status === 'complete' || status === 'auto-complete';
          });
        }

        setMonthlyTaskStatus(statusMap);
      }
    } catch (error) {
      console.error('Error fetching monthly task status:', error);
    }
  };

  const fetchPmsAndTransactionStatus = async () => {
    try {
      const statusByMonth = {};

      // Fetch PMS transfer confirmations
      const pmsResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/pms-transfers/status`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (pmsResponse.ok) {
        const pmsData = await pmsResponse.json();
        setPmsTransfersConfirmed(pmsData.confirmedMonths || {});
      }

      // Fetch transaction status for each month
      await Promise.all(monthlyTasks.map(async (month) => {
        const [year, monthNum] = month.value.split('-');

        statusByMonth[month.value] = {
          hasTransactions: false,
          hasUnconfirmed: false,
          allConfirmed: false,
          unconfirmedCount: 0,
          label: month.label
        };

        try {
          const monthStatusResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_API_URL}/expense/month-status?year=${year}&month=${monthNum}`,
            {
              method: 'GET',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' }
            }
          );

          if (monthStatusResponse.ok) {
            const monthStatusData = await monthStatusResponse.json();
            statusByMonth[month.value].hasTransactions = monthStatusData.hasTransactions || false;
            statusByMonth[month.value].hasUnconfirmed = monthStatusData.hasUnconfirmed || false;
            statusByMonth[month.value].allConfirmed = monthStatusData.allConfirmed || false;
            statusByMonth[month.value].unconfirmedCount = monthStatusData.unconfirmedCount || 0;
          }
        } catch (error) {
          console.error(`Error fetching month status for ${month.label}:`, error);
        }
      }));

      setMonthlyTransactionStatus(statusByMonth);
    } catch (error) {
      console.error('Error fetching PMS and transaction status:', error);
    }
  };

  const fetchMonthlyCycleSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setMonthlyCycleSettings(data);
        if (data.cycleStartMonth) {
          const [year, month] = data.cycleStartMonth.split('-');
          setSelectedStartYear(parseInt(year));
          setSelectedStartMonth(month);
        }
      }
    } catch (error) {
      console.error('Error fetching monthly cycle settings:', error);
    }
  };

  const handleSaveMonthlyCycleSettings = async () => {
    setSavingCycleSettings(true);
    try {
      const cycleStartMonth = `${selectedStartYear}-${selectedStartMonth}`;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cycleStartMonth,
            isPaused: monthlyCycleSettings.isPaused
          })
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMonthlyCycleSettings(data.settings);
          setShowMonthlyCycleModal(false);
          alert('Monthly cycle settings saved successfully!');
        }
      }
    } catch (error) {
      console.error('Error saving monthly cycle settings:', error);
      alert('Failed to save monthly cycle settings. Please try again.');
    } finally {
      setSavingCycleSettings(false);
    }
  };

  const handleTogglePause = async () => {
    setSavingCycleSettings(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            isPaused: !monthlyCycleSettings.isPaused
          })
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMonthlyCycleSettings(data.settings);
          alert(`Monthly cycle ${data.settings.isPaused ? 'paused' : 'resumed'} successfully!`);
        }
      }
    } catch (error) {
      console.error('Error toggling monthly cycle pause:', error);
      alert('Failed to update monthly cycle. Please try again.');
    } finally {
      setSavingCycleSettings(false);
    }
  };

  const handleMarkMonthComplete = async (monthYear) => {
    if (!window.confirm(`Mark ${monthYear} as complete? This will override automatic detection.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/month-status/month-completion-status`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monthYear, status: 'complete' })
        }
      );

      if (response.ok) {
        // Refresh the monthly task status
        await fetchMonthlyTaskStatus();
      } else {
        throw new Error('Failed to mark month as complete');
      }
    } catch (error) {
      console.error('Error marking month as complete:', error);
      alert('Failed to mark month as complete. Please try again.');
    }
  };

  const handleMarkMonthIncomplete = async (monthYear) => {
    if (!window.confirm(`Mark ${monthYear} as incomplete? This will move it back to pending tasks.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/month-status/month-completion-status`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monthYear, status: 'incomplete' })
        }
      );

      if (response.ok) {
        // Refresh the monthly task status
        await fetchMonthlyTaskStatus();
      } else {
        throw new Error('Failed to mark month as incomplete');
      }
    } catch (error) {
      console.error('Error marking month as incomplete:', error);
      alert('Failed to mark month as incomplete. Please try again.');
    }
  };

  const handleManuallyConfirmPmsTransfer = async (monthValue) => {
    if (!window.confirm(`Manually confirm PMS transfer for ${monthValue}? This will mark it as complete without uploading transfers.`)) {
      return;
    }

    try {
      const [year, month] = monthValue.split('-');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/pms-transfers/confirm`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ year: parseInt(year), month: parseInt(month), transfers: {}, confirmed: true })
        }
      );

      if (response.ok) {
        await fetchPmsAndTransactionStatus();
      } else {
        throw new Error('Failed to confirm PMS transfer');
      }
    } catch (error) {
      console.error('Error confirming PMS transfer:', error);
      alert('Failed to confirm PMS transfer. Please try again.');
    }
  };

  const updateTaskStatus = async (taskId, status, additionalData = {}) => {
    try {
      console.log("updateTaskStatus - Task ID:", taskId);
      console.log("updateTaskStatus - Status:", status);
      console.log("updateTaskStatus - Additional Data:", additionalData);

      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/tasks/${taskId}`,
        {
          status,
          ...additionalData
        }
      );

      if (response.ok) {
        const updatedTask = await response.json();
        console.log("updateTaskStatus - Updated Task from backend:", updatedTask);

        setAdminTasks(prev => {
          // Handle object structure with tasks array
          if (prev && prev.tasks && Array.isArray(prev.tasks)) {
            return {
              ...prev,
              tasks: prev.tasks.map(task =>
                task.id === taskId ? (updatedTask.task || updatedTask) : task
              )
            };
          }
          // Handle direct array structure
          else if (Array.isArray(prev)) {
            return prev.map(task =>
              task.id === taskId ? (updatedTask.task || updatedTask) : task
            );
          }
          // Fallback for unexpected structure
          else {
            console.warn('Unexpected adminTasks structure:', prev);
            return prev;
          }
        });

        // Clear task inputs after successful update
        setTaskInputs(prev => {
          const newInputs = { ...prev };
          delete newInputs[taskId];
          return newInputs;
        });

        // Send notification to founder if task is completed
        if (status === 'completed') {
          await notifyFounder(taskId);
        }
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      alert('Failed to update task status. Please try again.');
    }
  };

  const notifyFounder = async (taskId) => {
    try {
      await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/tasks/${taskId}/notify`
      );
    } catch (error) {
      console.error('Error sending founder notification:', error);
    }
  };

  const handleTaskInputChange = (taskId, field, value) => {
    console.log(`Task ${taskId} input change: ${field} = ${value}`);
    setTaskInputs(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value
      }
    }));
  };

  const handleTaskFileUpload = async (taskId, file, field) => {
    if (!file) return;

    setUploadingFiles(prev => ({ ...prev, [taskId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', field);

      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/tasks/${taskId}/upload`,
        formData
        // Don't set Content-Type header - let the browser set it automatically with the boundary
      );

      if (response.ok) {
        const data = await response.json();
        // Store the filePath (GCS path) for attachments, not the signed URL
        handleTaskInputChange(taskId, field, data.filePath || data.fileName);
        alert('File uploaded successfully!');
      } else {
        alert('Failed to upload file');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const canCompleteTask = (taskName, taskId) => {
    const inputs = taskInputs[taskId] || {};

    switch (taskName) {
      case 'Set up Gmail account and domain':
        // Gmail task is now completed via the bulk email update button
        return false; // Disabled - use "Update All & Mark Complete" button instead
      case 'Form LLC':
        return true; // No validation required - completion sends email to client for certificate upload
      case 'Apply for EIN':
        return inputs.einDocument && inputs.einNumber;
      case 'SAM Registration Part 2':
        return inputs.ueiNumber && inputs.ueiNumber.trim() !== '';
      case 'Complete CAGE':
        return inputs.cageNumber;
      case 'eRA Registration':
        return inputs.eraUsername;
      case 'SAM Registration Part 1':
        return true; // No validation - just mark complete and send board minutes email
      case 'Confirm SO and FSR Assignment in eRA Commons':
        return true; // No validation - just mark complete and send SBA notification email
      case 'Grants.gov Registration':
        return true; // No validation - just mark complete and send AOR notification email
      case 'Confirm StreamLaunch Added as AOR in Grants.gov':
        return true; // No validation - just mark complete and send completion email
      default:
        return true;
    }
  };

  const handleMarkComplete = async (taskId, taskName) => {
    const inputs = taskInputs[taskId] || {};

    // For Gmail task, ensure company users are fetched before validation
    const isGmailTask = taskName.toLowerCase().includes('gmail') ||
                        taskName.toLowerCase().includes('email');

    if (isGmailTask && selectedSubmission && companyUsers.length === 0) {
      await fetchCompanyUsers(selectedSubmission['Company Name']);
    }

    if (!canCompleteTask(taskName, taskId)) {
      if (isGmailTask) {
        alert('Please ensure all founders\' emails have been updated to their company emails (not personal emails like Gmail, Yahoo, etc.) before marking this task as complete. Use the "Update User Emails" section to update all emails to company domains.');
      } else {
        alert('Please fill in all required fields before marking this task as complete.');
      }
      return;
    }

    // For Apply for EIN task, show email preview modal before marking complete
    if (taskName === 'Apply for EIN' && selectedSubmission) {
      const companyName = selectedSubmission['Company Name'];

      // Prepare EIN email variables
      setEinEmailVariables({
        companyName: companyName,
        einDocumentPath: inputs.einDocument,
        einNumber: inputs.einNumber,
        mercuryUrl: process.env.REACT_APP_MERCURY_REFERRAL_URL || 'https://mercury.com',
        clientTodolistUrl: `${window.location.origin}/client-todolist`
      });
      setEinTaskId(taskId);
      setShowEINEmailPreview(true);
      return;
    }

    // For SAM Registration Part 1 task, show board minutes email preview modal
    if (taskName === 'SAM Registration Part 1' && selectedSubmission) {
      const companyName = selectedSubmission['Company Name'];

      // Prepare board minutes email variables
      setBoardMinutesEmailData({
        companyName: companyName,
        clientTodolistUrl: `${window.location.origin}/client-todolist`
      });
      setBoardMinutesTaskId(taskId);
      setShowBoardMinutesEmailPreview(true);
      return;
    }

    // For Complete CAGE task, show email preview modal before marking complete
    if (taskName === 'Complete CAGE' && selectedSubmission) {
      const companyName = selectedSubmission['Company Name'];

      // Get UEI number from the SAM Registration Part 2 task
      const ueiTask = selectedSubmission ? getTasksBySubmission(selectedSubmission.ID).find(t => t.taskName === 'SAM Registration Part 2') : null;
      const ueiNumber = ueiTask?.ueiNumber || '';
      
      console.log("cage ueiNumber: ", ueiNumber);

      // Prepare CAGE email variables
      setCageEmailVariables({
        companyName: companyName,
        ueiNumber: ueiNumber,
        cageNumber: inputs.cageNumber
      });
      setCageTaskId(taskId);
      setShowCAGEEmailPreview(true);
      return;
    }

    // For eRA Registration task, show email preview modal before marking complete
    if (taskName === 'eRA Registration' && selectedSubmission) {
      const companyName = selectedSubmission['Company Name'];

      // Get the founder's company email from the first founder in the submission
      const founders = getSubmissionFounders(selectedSubmission);
      // Get the first founder's email (which should be their company email after Gmail setup)
      const founderCompanyEmail = founders[0]?.email || '';

      // Get the SO/FSR last name from companyData
      // The soAor field contains the admin username/email who is the SO/FSR
      let soFsrLastName = '';
      if (companyData && companyData.soAor) {
        // Find the admin in our admins list to get their name
        const soFsrAdmin = admins.find(a => a.username === companyData.soAor || a.email === companyData.soAor);
        if (soFsrAdmin && soFsrAdmin.name) {
          // Extract last name from full name
          const nameParts = soFsrAdmin.name.split(' ');
          soFsrLastName = nameParts[nameParts.length - 1] || soFsrAdmin.name;
        } else if (companyData.soAor) {
          // Fallback: extract from email if name not available
          const emailParts = companyData.soAor.split('@')[0].split('.');
          soFsrLastName = emailParts[emailParts.length - 1] || companyData.soAor;
        }
      }

      // Prepare eRA email variables
      setEraEmailVariables({
        companyName: companyName,
        eraAccountEmail: founderCompanyEmail,
        eraUsername: inputs.eraUsername,
        adminLastName: soFsrLastName,
        clientTodolistUrl: `${window.location.origin}/client-todolist`
      });
      setEraTaskId(taskId);
      setShowERAEmailPreview(true);
      return;
    }

    // For Confirm SO and FSR Assignment task, show SBA notification email preview modal
    if (taskName === 'Confirm SO and FSR Assignment in eRA Commons' && selectedSubmission) {
      const companyName = selectedSubmission['Company Name'];

      // Get UEI and EIN from previous tasks (using same pattern as CAGE email)
      const ueiTask = selectedSubmission ? getTasksBySubmission(selectedSubmission.ID).find(t => t.taskName === 'SAM Registration Part 2') : null;
      const einTask = selectedSubmission ? getTasksBySubmission(selectedSubmission.ID).find(t => t.taskName === 'Apply for EIN') : null;

      const ueiNumber = ueiTask?.ueiNumber || '';
      const einNumber = einTask?.einNumber || '';

      // Read business address directly from the parsed companyData on the submission.
      // Falls back to the new-company business address for not-yet-formed companies.
      const submissionCompanyData = selectedSubmission.companyData || {};
      const companyAddress =
        submissionCompanyData.businessAddress ||
        submissionCompanyData.newCompanyBusinessAddress ||
        '';

      // Prepare SBA notification email variables
      setSbaEmailVariables({
        companyName: companyName,
        companyAddress: companyAddress || '[Address TBD]',
        ueiNumber: ueiNumber,
        einNumber: einNumber,
        clientTodolistUrl: `${window.location.origin}/client-todolist`
      });
      setSoFsrTaskId(taskId);
      setShowSBANotificationPreview(true);
      return;
    }

    // For Grants.gov Registration task, show AOR notification email preview modal
    if (taskName === 'Grants.gov Registration' && selectedSubmission) {
      const companyName = selectedSubmission['Company Name'];

      // Get UEI from SAM Registration Part 2 task
      const tasks = getTasksBySubmission(selectedSubmission.ID);
      const ueiTask = tasks.find(t => t.taskName === 'SAM Registration Part 2');
      const ueiNumber = ueiTask?.ueiNumber || '';

      // Prepare AOR notification email variables
      setAorEmailVariables({
        companyName: companyName,
        ueiNumber: ueiNumber,
        clientTodolistUrl: `${window.location.origin}/client-todolist`
      });
      setAorNotificationTaskId(taskId);
      setShowAORNotificationPreview(true);
      return;
    }

    // For Confirm AOR Assignment task, show completion email preview modal
    if (taskName === 'Confirm StreamLaunch Added as AOR in Grants.gov' && selectedSubmission) {
      const companyName = selectedSubmission['Company Name'];

      // Prepare completion email variables
      setAorConfirmEmailVariables({
        companyName: companyName
      });
      setAorConfirmTaskId(taskId);
      setShowAORConfirmationEmailPreview(true);
      return;
    }

    updateTaskStatus(taskId, 'completed', inputs);
  };

  const fetchBankInfo = async () => {
    try {
      const bankInfoResponse = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/bank-info`
      );

      if (bankInfoResponse.ok) {
        const bankInfoData = await bankInfoResponse.json();
        console.log('Refreshed bank info data:', bankInfoData);
        setBankInfo(bankInfoData.bankInfo || []);
      } else {
        console.error('Failed to fetch bank info:', bankInfoResponse.status);
      }
    } catch (error) {
      console.error('Error fetching bank info:', error);
    }
  };

  const handleEmailChange = async () => {
    if (!selectedSubmission || !newCompanyEmail) return;

    setEmailChangeLoading(true);
    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/${selectedSubmission.ID}/change-email`,
        { newEmail: newCompanyEmail }
      );

      if (response.ok) {
        // Update local state
        setSubmissions(prev => prev.map(sub =>
          sub.ID === selectedSubmission.ID
            ? { ...sub, 'Company Email': newCompanyEmail }
            : sub
        ));
        setSelectedSubmission(prev => ({ ...prev, 'Company Email': newCompanyEmail }));

        // Close modal and reset
        setShowEmailChangeModal(false);
        setNewCompanyEmail('');

        // Show success message (you could add a toast notification here)
        alert('Company email updated successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to update email: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error changing company email:', error);
      alert('Failed to update company email. Please try again.');
    }
    setEmailChangeLoading(false);
  };

  const openEmailChangeModal = () => {
    if (selectedSubmission) {
      setNewCompanyEmail(selectedSubmission['Company Email'] || '');
      setShowEmailChangeModal(true);
    }
  };

  const fetchCompanyUsers = async (companyName) => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/users`
      );

      if (response.ok) {
        const data = await response.json();
        // Filter to only show client users (founders)
        const clientUsers = (data.users || []).filter(u => u.role === 'founder' || u.role === 'client');
        setCompanyUsers(clientUsers);
      }
    } catch (error) {
      console.error('Error fetching company users:', error);
    }
  };

  const fetchCompanyData = async (companyName) => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/companies`);
      if (response.ok) {
        const companies = await response.json();
        console.log("fetchCompanyData - All Companies:", companies);
        const company = companies.find(c => c.name === companyName);
        console.log("fetchCompanyData - Company Data:", company);
        if (company) {
          setCompanyData(company);
        }
      }

      // Also fetch company domain from company-info
      const companyInfoResponse = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`
      );
      if (companyInfoResponse.ok) {
        const companyInfo = await companyInfoResponse.json();
        if (companyInfo.companyInfo?.companyDomain) {
          setCompanyDomain(companyInfo.companyInfo.companyDomain);
        } else {
          setCompanyDomain('');
        }
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/admins`);
      if (response.ok) {
        const data = await response.json();
        setAdmins(data.admins || data);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const handleSoAorEmailChange = async (email, companyName) => {
    // Save both SO/AOR email and name (username) to company data
    if (companyName) {
      try {
        // First, update the SO/AOR name/username
        const soAorResponse = await api.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/auth/update-company-so-aor`,
          {
            companyName: companyName,
            soAor: email
          }
        );

        if (!soAorResponse.ok) {
          const data = await soAorResponse.json();
          alert(data.error || 'Failed to update SO/AOR');
          return;
        }

        // Then, update the SO/AOR email
        const emailResponse = await api.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/auth/update-company-so-aor-email`,
          {
            companyName: companyName,
            soAorEmail: email
          }
        );

        if (emailResponse.ok) {
          // Refresh company data
          await fetchCompanyData(companyName);
        } else {
          const data = await emailResponse.json();
          alert(data.error || 'Failed to update SO/AOR email');
        }
      } catch (error) {
        console.error('Error updating SO/AOR:', error);
        alert('Failed to update SO/AOR');
      }
    }
  };

  const handleBulkEmailInputChange = (userEmail, newEmail) => {
    setBulkEmailUpdates(prev => ({
      ...prev,
      [userEmail]: newEmail
    }));
  };

  const handleSaveDomain = async (companyName) => {
    if (!companyDomain || !companyDomain.trim()) {
      alert('Please enter a domain');
      return;
    }

    // Validate domain format (basic check)
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(companyDomain.trim())) {
      alert('Please enter a valid domain (e.g., company.com)');
      return;
    }

    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/save-domain`,
        { companyDomain: companyDomain.trim() }
      );

      if (response.ok) {
        alert('Domain saved successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to save domain: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving domain:', error);
      alert('Failed to save domain. Please try again.');
    }
  };

  const handleGmailBulkUpdate = async (taskId) => {
    // Validate domain is entered
    if (!companyDomain || !companyDomain.trim()) {
      alert('Please enter the company domain before proceeding');
      return;
    }

    // Validate domain format (basic check)
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(companyDomain.trim())) {
      alert('Please enter a valid domain (e.g., company.com)');
      return;
    }

    // Validate all emails are filled and valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const updates = Object.entries(bulkEmailUpdates).filter(([_, newEmail]) => newEmail && newEmail.trim());

    if (updates.length === 0) {
      alert('Please enter at least one email address to update');
      return;
    }

    for (const [_, newEmail] of updates) {
      if (!emailRegex.test(newEmail.trim())) {
        alert('Please enter valid email addresses for all users');
        return;
      }
    }

    const emailUpdates = updates.map(([oldEmail, newEmail]) => {
      const user = companyUsers.find(u => u.userEmail === oldEmail);
      return {
        userId: user?.userEmail || oldEmail,
        newEmail: newEmail.trim(),
        oldEmail: oldEmail
      };
    });

    // Store data for both modals (include domain)
    setGmailBulkEmailData({
      taskId,
      emailUpdates,
      companyDomain: companyDomain.trim()
    });

    setGmailOriginalEmailData({
      taskId,
      emailUpdates
    });

    // Show the FIRST modal (for original emails)
    setShowGmailOriginalEmailPreview(true);
  };

  const handleSendGmailOriginalEmail = async (emailData) => {
    // Close the original email modal and show the company email modal
    setShowGmailOriginalEmailPreview(false);
    setSendingGmailOriginalEmail(false);

    // Store the original email data for later
    setGmailOriginalEmailData(prev => ({
      ...prev,
      originalEmailSubject: emailData.subject,
      originalEmailHtmlContent: emailData.htmlContent
    }));

    // Show the company email modal (second modal)
    setShowGmailBulkEmailPreview(true);
  };

  const handleSendGmailBulkEmail = async (emailData) => {
    try {
      setSendingGmailBulkEmail(true);

      // Update all emails with the edited templates from both modals
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/gmail-bulk-update`,
        {
          emailUpdates: gmailBulkEmailData.emailUpdates,
          companyEmailSubject: emailData.subject,
          companyEmailHtmlContent: emailData.htmlContent,
          originalEmailSubject: gmailOriginalEmailData.originalEmailSubject,
          originalEmailHtmlContent: gmailOriginalEmailData.originalEmailHtmlContent,
          companyDomain: gmailBulkEmailData.companyDomain
        }
      );

      if (response.ok) {
        // Mark the Gmail task as complete
        await updateTaskStatus(gmailBulkEmailData.taskId, 'completed');

        // Refresh company users
        if (selectedSubmission) {
          await fetchCompanyUsers(selectedSubmission['Company Name']);
        }

        // Clear bulk email updates and domain
        setBulkEmailUpdates({});
        setCompanyDomain('');
        setShowGmailBulkEmailPreview(false);
        setGmailBulkEmailData({});
        setGmailOriginalEmailData({});
        alert('Emails updated and verification emails sent to both old and new email addresses!');
      } else {
        const errorData = await response.json();
        alert(`Failed to update emails: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating bulk emails:', error);
      alert('Failed to update emails. Please try again.');
    } finally {
      setSendingGmailBulkEmail(false);
    }
  };

  const handleSendEINEmail = async (emailData) => {
    try {
      setSendingEINEmail(true);

      // Get the EIN data from task inputs
      const inputs = taskInputs[einTaskId] || {};

      console.log("EIN Email - Task ID:", einTaskId);
      console.log("EIN Email - Task Inputs:", inputs);
      console.log("EIN Email - EIN Document:", inputs.einDocument);
      console.log("EIN Email - EIN Number:", inputs.einNumber);

      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/tasks/${einTaskId}/send-ein-email`,
        {
          subject: emailData.subject,
          htmlContent: emailData.htmlContent,
          einDocument: inputs.einDocument, // Pass the EIN document path
          einNumber: inputs.einNumber // Pass the EIN number for reference
        }
      );

      if (response.ok) {
        console.log("EIN Email - Saving to task with data:", {
          einDocument: inputs.einDocument,
          einNumber: inputs.einNumber
        });

        // Mark the EIN task as complete with the EIN data
        await updateTaskStatus(einTaskId, 'completed', {
          einDocument: inputs.einDocument,
          einNumber: inputs.einNumber
        });

        // Refresh admin data to ensure the updated task with einNumber is available
        await fetchAdminData();

        setShowEINEmailPreview(false);
        setEinTaskId(null);
        alert('EIN email sent successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to send EIN email: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending EIN email:', error);
      alert('Failed to send EIN email. Please try again.');
    } finally {
      setSendingEINEmail(false);
    }
  };

  const handleSendCAGEEmail = async (emailData) => {
    try {
      setSendingCAGEEmail(true);

      // Get the CAGE data from task inputs
      const inputs = taskInputs[cageTaskId] || {};

      // Send the email via /emails/send endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/emails/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailData.recipientEmail,
          subject: emailData.subject,
          html: emailData.htmlContent,
          emailType: 'CAGE Complete'
        })
      });

      if (response.ok) {
        // Mark the CAGE task as complete with the CAGE data
        await updateTaskStatus(cageTaskId, 'completed', {
          cageDocument: inputs.cageDocument,
          cageNumber: inputs.cageNumber
        });

        setShowCAGEEmailPreview(false);
        setCageTaskId(null);
        alert('CAGE completion email sent successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to send CAGE email: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending CAGE email:', error);
      alert('Failed to send CAGE email. Please try again.');
    } finally {
      setSendingCAGEEmail(false);
    }
  };

  const handleSendERAEmail = async (emailData) => {
    try {
      setSendingERAEmail(true);

      // Get the eRA data from task inputs and email variables
      const inputs = taskInputs[eraTaskId] || {};

      // Calculate the date 7 days from now (day before the 8-day deadline)
      const reminderDate = new Date();
      reminderDate.setDate(reminderDate.getDate() + 7);

      // Send the email via /emails/send endpoint with scheduling for reminder
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/emails/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailData.recipientEmail,
          subject: emailData.subject,
          html: emailData.htmlContent,
          emailType: 'eRA Registration Complete',
          variables: eraEmailVariables,
          scheduleReminder: true,
          reminderDate: reminderDate.toISOString(),
          reminderSubject: `Reminder: Please add StreamLaunch as Signing Official in eRA Commons`,
          reminderMessage: `This is a reminder to complete the eRA Commons setup. Please add StreamLaunch as an additional Signing Official (SO) by tomorrow.`
        })
      });

      if (response.ok) {
        const responseData = await response.json();

        // Get the saved email URL to use as instructions
        const instructionsUrl = responseData.emailViewUrl
          ? `${process.env.REACT_APP_BACKEND_API_URL}/emails/view/${responseData.savedEmailId}`
          : null;

        // Mark the eRA task as complete with the eRA data and instructions URL
        // Use eraEmailVariables for the auto-populated fields
        await updateTaskStatus(eraTaskId, 'completed', {
          eraUsername: inputs.eraUsername,
          eraAccountEmail: eraEmailVariables.eraAccountEmail,
          adminLastName: eraEmailVariables.adminLastName,
          instructionsUrl: instructionsUrl
        });

        setShowERAEmailPreview(false);
        setEraTaskId(null);
        alert('eRA completion email sent successfully! A reminder email will be sent 7 days from now.');
      } else {
        const errorData = await response.json();
        alert(`Failed to send eRA email: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending eRA email:', error);
      alert('Failed to send eRA email. Please try again.');
    } finally {
      setSendingERAEmail(false);
    }
  };

  const handleSendSBANotification = async (emailData) => {
    try {
      setSendingSBANotification(true);

      // Send the email via /emails/send endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/emails/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailData.recipientEmail,
          subject: emailData.subject,
          html: emailData.htmlContent,
          emailType: 'SBA Registration Required'
        })
      });

      if (response.ok) {
        // Mark the SO/FSR task as complete
        await updateTaskStatus(soFsrTaskId, 'completed');

        setShowSBANotificationPreview(false);
        setSoFsrTaskId(null);
        alert('SO/FSR confirmation completed and SBA registration notification sent to client!');
      } else {
        const errorData = await response.json();
        alert(`Failed to send SBA notification: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending SBA notification:', error);
      alert('Failed to send SBA notification. Please try again.');
    } finally {
      setSendingSBANotification(false);
    }
  };

  const handleSendAORNotification = async (emailData) => {
    try {
      setSendingAORNotification(true);

      // Send the email via /emails/send endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/emails/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailData.recipientEmail,
          subject: emailData.subject,
          html: emailData.htmlContent,
          emailType: 'AOR Assignment Required',
          variables: aorEmailVariables
        })
      });

      if (response.ok) {
        const responseData = await response.json();

        // Get the saved email URL to use as instructions
        const instructionsUrl = responseData.emailViewUrl
          ? `${process.env.REACT_APP_BACKEND_API_URL}/emails/view/${responseData.savedEmailId}`
          : null;

        // Mark the Grants.gov task as complete with instructions URL
        await updateTaskStatus(aorNotificationTaskId, 'completed', {
          instructionsUrl: instructionsUrl
        });

        setShowAORNotificationPreview(false);
        setAorNotificationTaskId(null);
        alert('Grants.gov registration completed and AOR assignment notification sent to client!');
      } else {
        const errorData = await response.json();
        alert(`Failed to send AOR notification: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending AOR notification:', error);
      alert('Failed to send AOR notification. Please try again.');
    } finally {
      setSendingAORNotification(false);
    }
  };

  const handleSendAORConfirmationEmail = async (emailData) => {
    try {
      setSendingAORConfirmationEmail(true);

      // Send the email via /emails/send endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/emails/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailData.recipientEmail,
          subject: emailData.subject,
          html: emailData.htmlContent,
          emailType: 'SBIR Preparation Complete'
        })
      });

      if (response.ok) {
        // Mark the AOR confirmation task as complete
        await updateTaskStatus(aorConfirmTaskId, 'completed');

        setShowAORConfirmationEmailPreview(false);
        setAorConfirmTaskId(null);
        alert('AOR confirmation completed and preparation completion email sent to client!');
      } else {
        const errorData = await response.json();
        alert(`Failed to send completion email: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending AOR confirmation email:', error);
      alert('Failed to send AOR confirmation email. Please try again.');
    } finally {
      setSendingAORConfirmationEmail(false);
    }
  };

  const handleSendBoardMinutesEmail = async (emailData) => {
    try {
      setSendingBoardMinutesEmail(true);

      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/tasks/${boardMinutesTaskId}/send-board-minutes-email`,
        {
          subject: emailData.subject,
          htmlContent: emailData.htmlContent
        }
      );

      if (response.ok) {
        // If this email is being sent as part of SAM Part 1 completion, mark the task as complete
        if (boardMinutesTaskId) {
          await updateTaskStatus(boardMinutesTaskId, 'completed', {});
        }

        setShowBoardMinutesEmailPreview(false);
        setBoardMinutesEmailData({});
        setBoardMinutesTaskId(null);
        alert('Board minutes email sent to all founders with document attachment!');

        // Refresh tasks
        await fetchAdminData();
      } else {
        const errorData = await response.json();
        alert(`Failed to send board minutes email: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending board minutes email:', error);
      alert('Failed to send board minutes email. Please try again.');
    } finally {
      setSendingBoardMinutesEmail(false);
    }
  };

  const isTaskLocked = (task, allTasks) => {
    const taskName = task.taskName;

    // Find Form LLC task
    const formLLCTask = allTasks.find(t => t.taskName === 'Form LLC');
    const cofUploaded = formLLCTask?.cofUploaded === 'true' || task.cofUploaded === 'true';

    // Find Apply for EIN task
    const einTask = allTasks.find(t => t.taskName === 'Apply for EIN');
    const einCompleted = einTask?.status === 'completed';

    // Find SAM Part 1 task
    const samPart1Task = allTasks.find(t => t.taskName === 'SAM Registration Part 1');
    const samPart1Completed = samPart1Task?.status === 'completed';

    // Find SAM Registration Part 2 task for UEI number
    const samPart2Task = allTasks.find(t => t.taskName === 'SAM Registration Part 2');

    const ueiNumber = samPart2Task?.ueiNumber || task.ueiNumber;

    // Check bank info
    const bankInfoSubmitted = task.bankInfoSubmitted === 'true';

    // Task-specific locking rules
    switch (taskName) {
      case 'Set up Gmail account and domain':
      case 'Form LLC':
        return false; // These are never locked

      case 'Apply for EIN':
        // Locked until COF is uploaded by founders AND Form LLC is complete
        const formLLCCompleted = formLLCTask?.status === 'completed';
        if (!formLLCCompleted) {
          return { locked: true, reason: 'Waiting for Form LLC to be completed' };
        }
        if (!cofUploaded) {
          return { locked: true, reason: 'Waiting for Certificate of Formation from founders' };
        }
        return false;

      case 'SAM Registration Part 1':
        // Locked until EIN is complete
        if (!einCompleted) {
          return { locked: true, reason: 'Waiting for Apply for EIN to be completed' };
        }
        return false;

      case 'SAM Registration Part 2':
        // Locked until SAM Part 1 is complete
        if (!samPart1Completed) {
          return { locked: true, reason: 'Waiting for SAM Registration Part 1 to be completed' };
        }
        return false;

      case 'CAGE – Continuation of SAM Registration':
        // Locked until SAM Part 2 is complete AND bank account information is submitted
        const samPart2Completed = samPart2Task?.status === 'completed';
        if (!samPart2Completed) {
          return { locked: true, reason: 'Waiting for SAM Registration Part 2 to be completed' };
        }
        if (!bankInfoSubmitted) {
          return { locked: true, reason: 'Waiting for founders to submit bank account information' };
        }
        return false;

      case 'Complete CAGE':
        // Locked until CAGE Continuation is complete, bank info is submitted, UEI number exists, 
        // certificate of formation is uploaded, and board meeting minutes are signed
        const cageContinuationTask = allTasks.find(t => t.taskName === 'CAGE – Continuation of SAM Registration');
        const cageContinuationCompleted = cageContinuationTask?.status === 'completed';
        
        // Check if certificate of formation has been uploaded by founders
        const cofUploadedForCage = cofUploaded;
        
        // Board minutes is a FOUNDER task, check the boardMinutesSigned field that gets set when founder uploads
        // This field is set on multiple admin tasks when the founder completes their upload
        const boardMinutesSigned = task.boardMinutesSigned === 'true';

        if (!cageContinuationCompleted) {
          return { locked: true, reason: 'Waiting for CAGE – Continuation of SAM Registration to be completed' };
        }
        if (!cofUploadedForCage) {
          return { locked: true, reason: 'Waiting for founders to upload Certificate of Formation' };
        }
        if (!boardMinutesSigned) {
          return { locked: true, reason: 'Waiting for founders to sign and upload board meeting minutes' };
        }
        if (!bankInfoSubmitted || !ueiNumber) {
          return { locked: true, reason: 'Waiting for founders to submit bank account info and UEI number to be obtained' };
        }
        return false;

      case 'eRA Registration':
        // Locked until Complete CAGE task is completed
        const completeCageTask = allTasks.find(t => t.taskName === 'Complete CAGE');
        const cageCompleted = completeCageTask?.status === 'completed';

        if (!cageCompleted) {
          return { locked: true, reason: 'Waiting for Complete CAGE task to be completed' };
        }
        return false;

      case 'Confirm SO and FSR Assignment in eRA Commons':
        // Locked until eRA Registration is completed
        const eraRegistrationTask = allTasks.find(t => t.taskName === 'eRA Registration');
        const eraCompleted = eraRegistrationTask?.status === 'completed';

        if (!eraCompleted) {
          return { locked: true, reason: 'Waiting for eRA Registration to be completed' };
        }
        return false;

      case 'Grants.gov Registration':
        // Locked until SO/FSR confirmation is completed
        const soFsrTask = allTasks.find(t => t.taskName === 'Confirm SO and FSR Assignment in eRA Commons');
        const soFsrCompleted = soFsrTask?.status === 'completed';

        if (!soFsrCompleted) {
          return { locked: true, reason: 'Waiting for SO and FSR Assignment confirmation in eRA Commons' };
        }
        return false;

      case 'Confirm StreamLaunch Added as AOR in Grants.gov':
        // Locked until Grants.gov registration is completed
        const grantsGovTask = allTasks.find(t => t.taskName === 'Grants.gov Registration');
        const grantsGovCompleted = grantsGovTask?.status === 'completed';

        if (!grantsGovCompleted) {
          return { locked: true, reason: 'Waiting for Grants.gov Registration to be completed' };
        }

        return false;

      default:
        return false;
    }
  };

  const canSubmitUEIForm = (taskId) => {
    const formData = ueiFormData[taskId];
    if (!formData) return false;

    if (formData.ueiReceived === 'yes') {
      return formData.ueiNumber && formData.ueiNumber.trim() !== '';
    } else if (formData.ueiReceived === 'no') {
      return formData.boardMinutesRequired === 'yes' || formData.boardMinutesRequired === 'no';
    }
    return false;
  };

  const handleSubmitUEIForm = async (taskId) => {
    const formData = ueiFormData[taskId];
    if (!canSubmitUEIForm(taskId)) {
      alert('Please complete all required fields');
      return;
    }

    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/uei-info/${taskId}`,
        {
          ueiReceived: formData.ueiReceived === 'yes',
          ueiNumber: formData.ueiNumber || '',
          boardMinutesRequired: formData.boardMinutesRequired === 'yes'
        }
      );

      if (response.ok) {
        const data = await response.json();

        // If board minutes email should be sent, show the preview modal
        if (data.shouldSendBoardMinutesEmail) {
          setBoardMinutesEmailData({
            companyName: selectedSubmission['Company Name'],
            clientTodolistUrl: `${window.location.origin}/client-todolist`
          });
          setBoardMinutesTaskId(taskId);
          setShowBoardMinutesEmailPreview(true);
        } else {
          // Refresh tasks if no email needs to be sent
          await fetchAdminData();

          // Clear form data
          setUeiFormData(prev => {
            const newData = { ...prev };
            delete newData[taskId];
            return newData;
          });

          alert('UEI information submitted successfully!');
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to submit UEI info: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error submitting UEI form:', error);
      alert('Failed to submit UEI information. Please try again.');
    }
  };

  // Custom admin tasks functions
  const fetchCustomTasks = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks`
      );
      if (response.ok) {
        const data = await response.json();
        setCustomTasks(data.tasks || []);
        setCustomTaskCompletions(data.completions || []);
      }
    } catch (error) {
      console.error('Error fetching custom tasks:', error);
    }
  };

  const handleCreateCustomTask = async () => {
    if (!newTask.title) {
      alert('Task title is required');
      return;
    }
    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks`,
        {
          ...newTask,
          fileUploadItems: JSON.stringify(newTask.fileUploadItems),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCustomTasks(prev => [...prev, data.task]);
        setShowCreateTaskModal(false);
        setNewTask({
          title: '', description: '', isRepeated: false, repeatDate: '',
          cycle: 'monthly', assignedTo: 'admin', taskType: 'mark-as-complete', fileUploadItems: [],
        });
      } else {
        alert('Failed to create task');
      }
    } catch (error) {
      console.error('Error creating custom task:', error);
      alert('Failed to create task');
    }
  };

  const handleUpdateCustomTask = async (taskId, updates) => {
    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/${taskId}`,
        updates
      );
      if (response.ok) {
        const data = await response.json();
        setCustomTasks(prev => prev.map(t => t.id === taskId ? data.task : t));
      }
    } catch (error) {
      console.error('Error updating custom task:', error);
      alert('Failed to update task');
    }
  };

  const handleCompleteCustomTask = async (taskId) => {
    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/${taskId}/complete`,
        { periodKey: new Date().toISOString().slice(0, 7) }
      );
      if (response.ok) {
        const data = await response.json();
        setCustomTaskCompletions(prev => [...prev, data.completion]);
      }
    } catch (error) {
      console.error('Error completing custom task:', error);
      alert('Failed to complete task');
    }
  };

  const handleUploadCustomTaskFile = async (taskId, file, uploadItemIndex = null, uploadItemName = null) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('periodKey', new Date().toISOString().slice(0, 7));
      if (uploadItemIndex !== null) {
        formData.append('uploadItemIndex', uploadItemIndex);
        formData.append('uploadItemName', uploadItemName || '');
      }
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/${taskId}/upload`,
        formData
      );
      if (response.ok) {
        const data = await response.json();
        setCustomTaskCompletions(prev => [...prev, data.completion]);
        alert('File uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading task file:', error);
      alert('Failed to upload file');
    }
  };

  const handleUncompleteCustomTask = async (taskId, periodKey) => {
    if (!window.confirm('Mark this task as pending? Previously uploaded files will be preserved.')) {
      return;
    }
    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/${taskId}/uncomplete`,
        { periodKey }
      );
      if (response.ok) {
        setCustomTaskCompletions(prev => prev.map(c =>
          (c.taskId === taskId && c.periodKey === periodKey) ? { ...c, status: 'reverted' } : c
        ));
      } else {
        throw new Error('Failed to uncomplete task');
      }
    } catch (error) {
      console.error('Error uncompleting custom task:', error);
      alert('Failed to mark task as pending');
    }
  };

  const handleRevertPmsTransfer = async (monthValue) => {
    if (!window.confirm(`Mark PMS transfer for ${monthValue} as pending? This will remove the confirmation.`)) {
      return;
    }
    try {
      const [year, month] = monthValue.split('-');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/pms-transfers/confirm`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ year: parseInt(year), month: parseInt(month) })
        }
      );
      if (response.ok) {
        await fetchPmsAndTransactionStatus();
      } else {
        throw new Error('Failed to revert PMS transfer');
      }
    } catch (error) {
      console.error('Error reverting PMS transfer:', error);
      alert('Failed to revert PMS transfer');
    }
  };

  const getTasksBySubmission = (submissionId) => {
    console.log("admin tasks: ", adminTasks);
    console.log("submissionId: ", submissionId);
    console.log("filtered tasks: ", (adminTasks.tasks || []).filter(task => task.companyFormationId === submissionId));
    return (adminTasks.tasks || []).filter(task => task.companyFormationId === submissionId);
  };

  const getFilteredTasks = (tasks) => {
    if (!tasks || !Array.isArray(tasks)) return [];
    
    switch (taskFilter) {
      case 'pending':
        return tasks.filter(task => task.status === 'pending');
      case 'completed':
        return tasks.filter(task => task.status === 'completed');
      default:
        return tasks.filter(task => task);
    }
  };

  const getCompletionPercentage = (tasks) => {
    if (!tasks || !Array.isArray(tasks) || !tasks.length) return 0;
    const completedTasks = tasks.filter(task => task.status === 'completed').length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  // True once every formation task has been completed. Used to hide the
  // formation form button, Submission Details, and Bank Information boxes,
  // since that info then lives on the Company Information page.
  const isFormationComplete = () => {
    const allFormationTasks = adminTasks.tasks || adminTasks || [];
    return Array.isArray(allFormationTasks)
      && allFormationTasks.length > 0
      && allFormationTasks.every(t => t.status === 'completed');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'in_progress':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (submission) => {
    const daysOld = Math.floor((new Date() - new Date(submission['Submitted At'])) / (1000 * 60 * 60 * 24));
    if (daysOld > 7) return 'border-red-200 bg-red-50';
    if (daysOld > 3) return 'border-yellow-200 bg-yellow-50';
    return 'border-gray-200 bg-gray-50';
  };

  if (!userInfo || !['admin', 'superadmin'].includes(userInfo.accountType)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-center text-gray-600">This page is only available for administrators.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="card p-6 max-w-md mx-auto">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading admin dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="card p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Todolist</h1>
              <p className="text-gray-600 mt-1">
                Manage incomplete company tasks
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">{submissions.length}</div>
              <div className="text-sm text-gray-500">Active Submissions</div>
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Task Settings Button - Above Tasks Section */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate('/task-settings')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Manage Tasks
          </button>
          <button
            onClick={() => navigate('/task-documents')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Task Documents
          </button>
          <button
            onClick={() => setShowMonthlyCycleModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title="Start Monthly Cycle"
          >
            Start Monthly Cycle
          </button>
        </div>

        

        {/* Monthly Tasks Section */}
        <div className="card p-6">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => setMonthlyTasksExpanded(!monthlyTasksExpanded)}
          >
            <h2 className="text-xl font-semibold text-gray-900">Tasks</h2>
            <span className="text-gray-500">
              {monthlyTasksExpanded ? '▼' : '▶'}
            </span>
          </div>
          <p className="text-gray-600 text-sm mt-1 mb-4">Monthly bank statement and payroll uploads</p>

          {monthlyTasksExpanded && (
            <div className="space-y-4">
              {/* Empty state when no cycle started */}
              {monthlyTasks.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-500">Monthly tasks cycle has not been started yet.</p>
                  <p className="text-gray-400 text-sm mt-1">Use the "Start Monthly Cycle" button above to begin tracking monthly tasks.</p>
                </div>
              ) : (
              <>
              {/* Bank Statement Upload Tasks - Pending Only */}
              {(() => {
                const pendingMonths = monthlyTasks.filter(month => !monthlyTaskStatus[month.value]);
                
                if (pendingMonths.length === 0) {
                  return (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Bank Statement Uploads</h3>
                      <p className="text-gray-500 text-sm py-2">All bank statement uploads are complete!</p>
                    </div>
                  );
                }
                
                return (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Bank Statement Uploads</h3>
                <div className="space-y-2">
                  {pendingMonths.map((month) => {
                    return (
                      <div
                        key={`upload-${month.value}`}
                        className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50"
                      >
                        <Link
                          to={`/grant-fund-financials?section=financials-import&month=${month.value}`}
                          className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
                        >
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">Upload Bank Statements: {month.label}</p>
                            <p className="text-sm text-gray-600">Pending upload</p>
                          </div>
                        </Link>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.preventDefault(); handleMarkMonthComplete(month.value); }}
                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Mark as complete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
                );
              })()}

              {/* PMS Statement Tasks - Full Workflow */}
              {(() => {
                // Months waiting for statement upload
                const monthsWaitingUpload = monthlyTasks.filter(month => {
                  const txStatus = monthlyTransactionStatus[month.value];
                  return !txStatus?.hasTransactions;
                });

                // Months waiting for client confirmation
                const monthsWaitingConfirmation = monthlyTasks.filter(month => {
                  const txStatus = monthlyTransactionStatus[month.value];
                  return txStatus?.hasTransactions && txStatus?.hasUnconfirmed;
                });

                // Months ready for PMS
                const pendingPmsMonths = monthlyTasks.filter(month => {
                  const txStatus = monthlyTransactionStatus[month.value];
                  return txStatus?.allConfirmed && !pmsTransfersConfirmed[month.value];
                });

                return (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Confirm PMS Transfer</h3>
                    <div className="space-y-2">
                      {/* Months waiting for upload */}
                      {monthsWaitingUpload.map((month) => (
                        <div key={`pms-upload-${month.value}`} className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50">
                          <Link
                            to={`/pms-payments?month=${month.value}`}
                            className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
                          >
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </span>
                            <div>
                              <p className="font-medium text-gray-900">Confirm PMS Transfer: {month.label}</p>
                              <p className="text-sm text-gray-500">Waiting for statement upload</p>
                            </div>
                          </Link>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => { e.preventDefault(); handleManuallyConfirmPmsTransfer(month.value); }}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Manually confirm PMS transfer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      ))}

                      {/* Months waiting for client confirmation */}
                      {monthsWaitingConfirmation.map((month) => {
                        const txStatus = monthlyTransactionStatus[month.value];
                        return (
                          <div key={`pms-confirm-${month.value}`} className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50">
                            <Link
                              to={`/pms-payments?month=${month.value}`}
                              className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
                            >
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </span>
                              <div>
                                <p className="font-medium text-gray-900">Confirm PMS Transfer: {month.label}</p>
                                <p className="text-sm text-amber-700">
                                  Waiting for client to confirm {txStatus?.unconfirmedCount || 0} transaction{txStatus?.unconfirmedCount !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </Link>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => { e.preventDefault(); handleManuallyConfirmPmsTransfer(month.value); }}
                                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                title="Manually confirm PMS transfer"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        );
                      })}

                      {/* Months ready for PMS */}
                      {pendingPmsMonths.map((month) => (
                        <div key={`pms-ready-${month.value}`} className="flex items-center justify-between p-4 rounded-lg border border-blue-200 bg-blue-50">
                          <Link
                            to={`/pms-payments?month=${month.value}`}
                            className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
                          >
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                            </span>
                            <div>
                              <p className="font-medium text-gray-900">Confirm PMS Transfer: {month.label}</p>
                              <p className="text-sm text-blue-700">Ready - All transactions confirmed</p>
                            </div>
                          </Link>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => { e.preventDefault(); handleManuallyConfirmPmsTransfer(month.value); }}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Manually confirm PMS transfer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      ))}

                      {/* Empty state */}
                      {monthsWaitingUpload.length === 0 && monthsWaitingConfirmation.length === 0 &&
                       pendingPmsMonths.length === 0 && (
                        <p className="text-gray-500 text-sm py-2">No PMS transfer tasks available.</p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Annual Default Tasks (each with its own header) - only show pending ones */}
              {(() => {
                const defaultTasks = customTasks.filter(t => t.status === 'active' && t.taskType !== 'special' && t.isDefault === 'true');
                if (defaultTasks.length === 0) return null;

                const currentYear = new Date().getFullYear().toString();
                // Filter to only show tasks not completed this year
                const pendingDefaultTasks = defaultTasks.filter(task => {
                  const taskCompletions = customTaskCompletions.filter(c => c.taskId === task.id && c.status !== 'reverted');
                  return !taskCompletions.some(c => c.periodKey?.startsWith(currentYear));
                });
                if (pendingDefaultTasks.length === 0) return null;

                return (
                  <>
                    {pendingDefaultTasks.map(task => {
                      const taskCompletions = customTaskCompletions.filter(c => c.taskId === task.id && c.status !== 'reverted');

                      return (
                        <div key={task.id}>
                          <h3 className="text-sm font-medium text-gray-700 mb-2">{task.title} - {currentYear}</h3>
                          <div className="space-y-2">
                            <div
                              className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50"
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </span>
                                <div>
                                  <p className="font-medium text-gray-900">{task.description || task.title} - {currentYear}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">Yearly</span>
                                    {formatDueDate(task) && (
                                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">{formatDueDate(task)}</span>
                                    )}
                                    {taskCompletions.length > 0 && (
                                      <span className="text-xs text-gray-500">
                                        Last: {new Date(taskCompletions[taskCompletions.length - 1].completedAt).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleCompleteCustomTask(task.id)}
                                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                              >
                                Mark Complete
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              })()}

              {/* Custom Tasks (user-created) - only show pending ones */}
              {(() => {
                const displayableTasks = customTasks.filter(t => t.status === 'active' && t.taskType !== 'special' && t.isDefault !== 'true');
                if (displayableTasks.length === 0) return null;

                const currentPeriod = new Date().toISOString().slice(0, 7);

                // Helper to check if a custom task is fully completed this period
                const isCustomTaskCompleted = (task) => {
                  const taskCompletions = customTaskCompletions.filter(c => c.taskId === task.id && c.status !== 'reverted');
                  if (task.taskType === 'file-upload') {
                    let uploadItems = [];
                    try {
                      if (task.fileUploadItems) {
                        uploadItems = typeof task.fileUploadItems === 'string'
                          ? JSON.parse(task.fileUploadItems)
                          : task.fileUploadItems;
                      }
                    } catch (e) { /* ignore */ }
                    if (uploadItems.length > 0) {
                      // All upload items must have completions for this period
                      return uploadItems.every((_, idx) =>
                        taskCompletions.some(c => c.periodKey === currentPeriod && String(c.uploadItemIndex) === String(idx))
                      );
                    }
                    // No specific items - any completion counts
                    return taskCompletions.some(c => c.periodKey === currentPeriod);
                  }
                  return taskCompletions.some(c => c.periodKey === currentPeriod);
                };

                // Filter to only pending tasks
                const pendingTasks = displayableTasks.filter(task => !isCustomTaskCompleted(task));
                if (pendingTasks.length === 0) return null;

                return (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      Custom Tasks
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                        {pendingTasks.length} active
                      </span>
                    </h3>
                    <div className="space-y-2">
                      {pendingTasks.map(task => {
                        const taskCompletions = customTaskCompletions.filter(c => c.taskId === task.id);

                        // Parse fileUploadItems if it exists
                        let uploadItems = [];
                        try {
                          if (task.fileUploadItems) {
                            uploadItems = typeof task.fileUploadItems === 'string'
                              ? JSON.parse(task.fileUploadItems)
                              : task.fileUploadItems;
                          }
                        } catch (e) {
                          console.error('Error parsing fileUploadItems:', e);
                        }

                        return (
                          <div
                            key={task.id}
                            className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              </span>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900">
                                    {task.title}
                                    {task.isRepeated === 'true' && (() => {
                                      const now = new Date();
                                      if (task.cycle === 'yearly') {
                                        return ` - ${now.getFullYear()}`;
                                      } else {
                                        return ` - ${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()}`;
                                      }
                                    })()}
                                  </p>
                                  {task.isRepeated === 'true' && (
                                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                      {task.cycle === 'yearly' ? 'Yearly' : 'Monthly'}
                                    </span>
                                  )}
                                  {formatDueDate(task) && (
                                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">{formatDueDate(task)}</span>
                                  )}
                                </div>
                                {task.description && (
                                  <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                                )}
                                {taskCompletions.length > 0 && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Last completed: {new Date(taskCompletions[taskCompletions.length - 1].completedAt).toLocaleDateString()}
                                  </p>
                                )}

                                {/* Display individual file upload items */}
                                {task.taskType === 'file-upload' && uploadItems.length > 0 && (
                                  <div className="mt-3 space-y-2">
                                    <p className="text-xs font-medium text-gray-500">Required uploads:</p>
                                    {uploadItems.map((item, idx) => {
                                      const activeCompletion = taskCompletions.find(c => c.periodKey === currentPeriod && String(c.uploadItemIndex) === String(idx) && c.status !== 'reverted');
                                      const revertedCompletion = !activeCompletion && taskCompletions.find(c => c.periodKey === currentPeriod && String(c.uploadItemIndex) === String(idx) && c.status === 'reverted');
                                      let uploadedFileName = null;
                                      const completion = activeCompletion || revertedCompletion;
                                      if (completion) {
                                        try {
                                          const files = JSON.parse(completion.uploadedFiles || '[]');
                                          if (files.length > 0) uploadedFileName = files[0].originalName;
                                        } catch (e) { /* ignore */ }
                                      }
                                      return (
                                        <div key={idx} className="flex items-center justify-between bg-gray-50 rounded p-2">
                                          <span className="text-sm text-gray-700">{item}</span>
                                          {activeCompletion ? (
                                            <span className="text-xs text-green-600">✓ {uploadedFileName || 'Uploaded'}</span>
                                          ) : revertedCompletion ? (
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs text-gray-500">Previously: {uploadedFileName || 'uploaded'}</span>
                                              <label className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">
                                                Re-upload
                                                <input
                                                  type="file"
                                                  className="hidden"
                                                  onChange={(e) => handleUploadCustomTaskFile(task.id, e.target.files[0], idx, item)}
                                                />
                                              </label>
                                            </div>
                                          ) : (
                                            <label className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">
                                              Upload
                                              <input
                                                type="file"
                                                className="hidden"
                                                onChange={(e) => handleUploadCustomTaskFile(task.id, e.target.files[0], idx, item)}
                                              />
                                            </label>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              {task.taskType === 'mark-as-complete' && (
                                <button
                                  onClick={() => handleCompleteCustomTask(task.id)}
                                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                  Mark Complete
                                </button>
                              )}
                              {task.taskType === 'file-upload' && uploadItems.length === 0 && (
                                <label className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer">
                                  Upload File
                                  <input
                                    type="file"
                                    className="hidden"
                                    onChange={(e) => handleUploadCustomTaskFile(task.id, e.target.files[0])}
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              </>
              )}
            </div>
          )}
        </div>

        {console.log('skiponboarding:', companyData?.skipOnboarding) || null}
        {console.log('companyData:', companyData) || null}

        {/* Onboarding-Skipped Setup Task - only shown for companies that skipped onboarding,
            replaces the formation tasks panel and reminds admins to do the setup work
            that the founder onboarding form would have populated. Completion state is
            stored in localStorage per-company so admins can mark it done. */}
        {(companyData?.skipOnboarding === 'true' || companyData?.skipOnboarding === true) && (() => {
          const companyKey = selectedSubmission ? selectedSubmission['Company Name'] : (companyData?.companyName || 'default');
          const storageKey = `onboardingSetup_${companyKey}_completedAt`;
          const completedAt = (typeof window !== 'undefined') ? localStorage.getItem(storageKey) : null;
          if (completedAt) {
            // Setup panel disappears entirely once marked complete — the entry
            // moves to the completed tasks section further down the page.
            return null;
          }
          const markComplete = () => {
            localStorage.setItem(storageKey, new Date().toISOString());
            setOnboardingSetupTick(t => t + 1);
          };
          return (
            <div className="grid grid-cols-1 gap-6">
              <div className="card p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {selectedSubmission ? `Onboarding Setup - ${selectedSubmission['Company Name']}` : 'Onboarding Setup'}
                  </h2>
                </div>
                <div className="space-y-3">
                  <div className="border rounded-lg p-4 border-amber-200 bg-amber-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900">Complete onboarding setup for this company</p>
                          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">PENDING</span>
                        </div>
                        <p className="text-sm mt-1 text-gray-700">
                          This company's founder onboarding was skipped at creation. Please upload all registrations and fill out company info on their behalf.
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <button
                          onClick={() => navigate('/registrations')}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                          Upload Registrations
                        </button>
                        <button
                          onClick={() => navigate('/company-info')}
                          className="px-3 py-1 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors whitespace-nowrap"
                        >
                          Fill Out Company Info
                        </button>
                        <button
                          onClick={markComplete}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors whitespace-nowrap"
                        >
                          Mark as Completed
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Formation Tasks Panel - hidden for skip-onboarding companies or when all formation tasks are complete */}
        {!(companyData?.skipOnboarding === 'true' || companyData?.skipOnboarding === true) && (() => {
          const allFormationTasksCheck = adminTasks.tasks || adminTasks || [];
          const allComplete = Array.isArray(allFormationTasksCheck) && allFormationTasksCheck.length > 0 && allFormationTasksCheck.every(t => t.status === 'completed');
          return !allComplete;
        })() && (
        <div className="grid grid-cols-1 gap-6">
          <div className="card p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {selectedSubmission ? `Formation Tasks - ${selectedSubmission['Company Name']}` : 'Formation Tasks'}
                </h2>
                <button
                  onClick={() => navigate('/company-formation')}
                  className="px-3 py-1 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
                >
                  View Formation Form
                </button>
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {(() => {
                const allFormationTasks = adminTasks.tasks || adminTasks || [];
                const tasksToShow = Array.isArray(allFormationTasks)
                  ? allFormationTasks.filter(t => t.status !== 'completed')
                  : [];
                
                // Use all tasks for lock status checks (not just filtered tasks)
                const allTasksArray = Array.isArray(allFormationTasks) ? allFormationTasks : [];
                
                return tasksToShow && tasksToShow.length > 0 ? tasksToShow.map((task) => {
                  const isGmailTask = task.taskName.toLowerCase().includes('gmail') ||
                                      task.taskName.toLowerCase().includes('email') ||
                                      task.taskDescription.toLowerCase().includes('gmail');
                  const isExpanded = expandedGmailTask === task.id;

                  // Check if task is locked - pass ALL tasks to check dependency completion
                  const lockStatus = isTaskLocked(task, allTasksArray);
                  const isLocked = lockStatus && lockStatus.locked;
                  const shouldHide = lockStatus && lockStatus.hidden;

                  // Don't render hidden tasks
                  if (shouldHide) return null;

                  return (
                    <div
                      key={task.id}
                      className={`border rounded-lg p-4 ${isLocked ? 'border-gray-300 bg-gray-50 opacity-60' : 'border-gray-200'}`}
                      title={isLocked ? lockStatus.reason : ''}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            {isLocked && (
                              <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                            )}
                            <p className={`font-medium ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>{task.taskName}</p>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
                              {task.status ? task.status.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
                            </span>
                          </div>
                          <p className={`text-sm mt-1 ${isLocked ? 'text-gray-500' : 'text-gray-600'}`}>{task.taskDescription}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            Company: {task.companyName}
                          </p>
                          {isLocked && lockStatus.reason && (
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                              <p className="text-xs text-yellow-800 flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                {lockStatus.reason}
                              </p>
                            </div>
                          )}
                          {task.completedAt && (
                            <p className="text-xs text-green-600 mt-1">
                              Completed {new Date(task.completedAt).toLocaleDateString()}
                              {task.completedBy && ` by ${task.completedBy}`}
                            </p>
                          )}

                          {/* Task-specific inputs */}
                          {task.status === 'pending' && !isLocked && (
                            <>
                              {/* Form LLC - No longer requires file upload or date input */}

                              {/* Apply for EIN Inputs */}
                              {task.taskName === 'Apply for EIN' && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">Required Information:</p>
                                  <div className="space-y-2">
                                    <div>
                                      <label className="block text-xs text-gray-600 mb-1">EIN Document *</label>
                                      <input
                                        type="file"
                                        onChange={(e) => handleTaskFileUpload(task.id, e.target.files[0], 'einDocument')}
                                        disabled={uploadingFiles[task.id]}
                                        className="text-xs w-full"
                                      />
                                      {taskInputs[task.id]?.einDocument && (
                                        <p className="text-xs text-green-600 mt-1">✓ File uploaded</p>
                                      )}
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-600 mb-1">EIN Number *</label>
                                      <input
                                        type="text"
                                        value={taskInputs[task.id]?.einNumber || ''}
                                        onChange={(e) => handleTaskInputChange(task.id, 'einNumber', e.target.value)}
                                        placeholder="XX-XXXXXXX"
                                        className="text-xs px-2 py-1 border border-gray-300 rounded w-full"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* SAM Registration Part 2 Inputs */}
                              {task.taskName === 'SAM Registration Part 2' && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">Required Information:</p>
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">UEI Number *</label>
                                    <input
                                      type="text"
                                      value={taskInputs[task.id]?.ueiNumber || ''}
                                      onChange={(e) => handleTaskInputChange(task.id, 'ueiNumber', e.target.value)}
                                      placeholder="Enter UEI Number"
                                      className="text-xs px-2 py-1 border border-gray-300 rounded w-full"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Complete CAGE Inputs */}
                              {task.taskName === 'Complete CAGE' && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">Required Information:</p>
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">CAGE Number *</label>
                                    <input
                                      type="text"
                                      value={taskInputs[task.id]?.cageNumber || ''}
                                      onChange={(e) => handleTaskInputChange(task.id, 'cageNumber', e.target.value)}
                                      placeholder="Enter CAGE Number"
                                      className="text-xs px-2 py-1 border border-gray-300 rounded w-full"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* eRA Registration Inputs */}
                              {task.taskName === 'eRA Registration' && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">Required Information:</p>
                                  <div className="space-y-2">
                                    <div>
                                      <label className="block text-xs text-gray-600 mb-1">Founder's eRA Username *</label>
                                      <input
                                        type="text"
                                        value={taskInputs[task.id]?.eraUsername || ''}
                                        onChange={(e) => handleTaskInputChange(task.id, 'eraUsername', e.target.value)}
                                        placeholder="Enter Founder's eRA Username"
                                        className="text-xs px-2 py-1 border border-gray-300 rounded w-full"
                                      />
                                    </div>
                                    <p className="text-xs text-gray-500 italic">
                                      Note: The founder's company email and assigned SO/FSR last name will be automatically used in the notification email.
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* SBA Registration Inputs */}
                              {task.taskName === 'SBA Registration' && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">Required Information:</p>
                                  <div className="space-y-2">
                                    <div>
                                      <label className="block text-xs text-gray-600 mb-1">SBA Document *</label>
                                      <input
                                        type="file"
                                        onChange={(e) => handleTaskFileUpload(task.id, e.target.files[0], 'sbaDocument')}
                                        disabled={uploadingFiles[task.id]}
                                        className="text-xs w-full"
                                      />
                                      {taskInputs[task.id]?.sbaDocument && (
                                        <p className="text-xs text-green-600 mt-1">✓ File uploaded</p>
                                      )}
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-600 mb-1">SBA Number *</label>
                                      <input
                                        type="text"
                                        value={taskInputs[task.id]?.sbaNumber || ''}
                                        onChange={(e) => handleTaskInputChange(task.id, 'sbaNumber', e.target.value)}
                                        placeholder="Enter SBA Number"
                                        className="text-xs px-2 py-1 border border-gray-300 rounded w-full"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* Email Update Dropdown for Gmail Task */}
                          {isGmailTask && (
                            <div className="mt-3">
                              {/* SO/AOR Dropdown */}
                              <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <label className="block text-sm font-semibold text-gray-800 mb-2">
                                  Assigned SO/AOR:
                                </label>
                                <select
                                  value={companyData?.soAor || ''}
                                  onChange={(e) => handleSoAorEmailChange(e.target.value, task.companyName)}
                                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Select SO/AOR</option>
                                  {admins.map((admin) => (
                                    <option key={admin.username} value={admin.username}>
                                      {admin.name || admin.username} ({admin.username})
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Company Domain Input Field */}
                              <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <label className="block text-sm font-semibold text-gray-800 mb-2">
                                  Company Domain:
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={companyDomain}
                                    onChange={(e) => setCompanyDomain(e.target.value)}
                                    placeholder="e.g., company.com"
                                    className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <button
                                    onClick={() => handleSaveDomain(task.companyName)}
                                    className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                                  >
                                    Save
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Enter the purchased domain for the company's email and website
                                </p>
                              </div>

                              <button
                                onClick={() => {
                                  if (!isExpanded && task.companyName) {
                                    fetchCompanyUsers(task.companyName);
                                  }
                                  setExpandedGmailTask(isExpanded ? null : task.id);
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                              >
                                <svg className={`w-4 h-4 mr-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                Update User Emails
                              </button>

                              {isExpanded && (
                                <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                  <p className="text-xs text-gray-600 mb-3">
                                    Fill out new email addresses for all users below, then click "Update All & Mark Complete" to send verification emails and complete the Gmail setup task.
                                  </p>

                                  {companyUsers.length > 0 ? (
                                    <>
                                      <div className="space-y-2 mb-3">
                                        {companyUsers.map((user) => (
                                          <div key={user.userEmail} className="p-2 bg-white rounded border border-gray-200">
                                            <div className="flex items-start justify-between mb-1">
                                              <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-900">{user.userName || user.userEmail}</p>
                                                <p className="text-xs text-gray-500">Current: {user.userEmail}</p>
                                              </div>
                                            </div>
                                            <input
                                              type="email"
                                              value={bulkEmailUpdates[user.userEmail] || ''}
                                              onChange={(e) => handleBulkEmailInputChange(user.userEmail, e.target.value)}
                                              placeholder="New company email address"
                                              className="w-full text-xs px-2 py-1 border border-gray-300 rounded"
                                            />
                                          </div>
                                        ))}
                                      </div>
                                      <button
                                        onClick={() => handleGmailBulkUpdate(task.id)}
                                        className="w-full px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                                      >
                                        Update All & Mark Complete
                                      </button>
                                    </>
                                  ) : (
                                    <p className="text-xs text-gray-500">No users found for this company.</p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex space-x-2 ml-4">
                          {task.instructionsUrl && task.instructionsUrl.trim() !== '' && (
                            <a
                              href={task.instructionsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            >
                              Instructions
                            </a>
                          )}

                          {task.status === 'pending' && (
                            <button
                              onClick={() => handleMarkComplete(task.id, task.taskName)}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                              disabled={!canCompleteTask(task.taskName, task.id)}
                              style={{ opacity: canCompleteTask(task.taskName, task.id) ? 1 : 0.5 }}
                            >
                              Mark Complete
                            </button>
                          )}

                          {task.status === 'completed' && (
                            <button
                              onClick={() => updateTaskStatus(task.id, 'pending')}
                              className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                            >
                              Mark Pending
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-gray-500 text-center py-4">No tasks found</p>
                );
              })()}
              
              {(() => {
                const allFormationTasks2 = adminTasks.tasks || adminTasks || [];
                const tasksToShow = Array.isArray(allFormationTasks2)
                  ? allFormationTasks2.filter(t => t.status !== 'completed')
                  : [];

                if (tasksToShow.length === 0) {
                  return (
                    <p className="text-gray-500 text-center py-8">
                      No pending formation tasks
                    </p>
                  );
                }

                return null;
              })()}

            </div>
          </div>
        </div>
        )}

        {/* Completed Tasks Section */}
        <div className="card p-6">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => setCompletedTasksExpanded(!completedTasksExpanded)}
          >
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">Completed Tasks</h2>
              <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                {(() => {
                  const isSkipOnboarding = companyData?.skipOnboarding === 'true' || companyData?.skipOnboarding === true;
                  const allTasks = adminTasks.tasks || adminTasks || [];
                  const formationCompletedCount = isSkipOnboarding ? 0 : (Array.isArray(allTasks)
                    ? allTasks.filter(t => t.status === 'completed').length
                    : 0);
                  const monthlyCompletedCount = monthlyTasks.filter(month => monthlyTaskStatus[month.value]).length;
                  const pmsCompletedCount = monthlyTasks.filter(month => {
                    const txStatus = monthlyTransactionStatus[month.value];
                    return txStatus?.allConfirmed && pmsTransfersConfirmed[month.value];
                  }).length;
                  const currentYear = new Date().getFullYear().toString();
                  const currentPeriod = new Date().toISOString().slice(0, 7);
                  const defaultTasks = customTasks.filter(t => t.status === 'active' && t.taskType !== 'special' && t.isDefault === 'true');
                  const defaultCompletedCount = defaultTasks.filter(task => {
                    const tc = customTaskCompletions.filter(c => c.taskId === task.id && c.status !== 'reverted');
                    return tc.some(c => c.periodKey?.startsWith(currentYear));
                  }).length;
                  const displayableTasks = customTasks.filter(t => t.status === 'active' && t.taskType !== 'special' && t.isDefault !== 'true');
                  const customCompletedCount = displayableTasks.filter(task => {
                    const tc = customTaskCompletions.filter(c => c.taskId === task.id && c.status !== 'reverted');
                    if (task.taskType === 'file-upload') {
                      let uploadItems = [];
                      try {
                        if (task.fileUploadItems) {
                          uploadItems = typeof task.fileUploadItems === 'string'
                            ? JSON.parse(task.fileUploadItems) : task.fileUploadItems;
                        }
                      } catch (e) { /* ignore */ }
                      if (uploadItems.length > 0) {
                        return uploadItems.every((_, idx) =>
                          tc.some(c => c.periodKey === currentPeriod && String(c.uploadItemIndex) === String(idx))
                        );
                      }
                    }
                    return tc.some(c => c.periodKey === currentPeriod);
                  }).length;
                  const companyKeyForOnboarding = selectedSubmission ? selectedSubmission['Company Name'] : (companyData?.companyName || 'default');
                  const onboardingCompleted = (isSkipOnboarding && typeof window !== 'undefined' && localStorage.getItem(`onboardingSetup_${companyKeyForOnboarding}_completedAt`)) ? 1 : 0;
                  return formationCompletedCount + monthlyCompletedCount + pmsCompletedCount + defaultCompletedCount + customCompletedCount + onboardingCompleted;
                })()}
              </span>
            </div>
            <span className="text-gray-500">
              {completedTasksExpanded ? '▼' : '▶'}
            </span>
          </div>
          <p className="text-gray-600 text-sm mt-1">Tasks that have been completed</p>

          {completedTasksExpanded && (
            <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
              {/* Completed Onboarding Setup (for skip-onboarding companies) */}
              {(() => {
                const isSkip = companyData?.skipOnboarding === 'true' || companyData?.skipOnboarding === true;
                if (!isSkip) return null;
                const companyKey = selectedSubmission ? selectedSubmission['Company Name'] : (companyData?.companyName || 'default');
                const completedAt = (typeof window !== 'undefined') ? localStorage.getItem(`onboardingSetup_${companyKey}_completedAt`) : null;
                if (!completedAt) return null;
                const markPending = () => {
                  localStorage.removeItem(`onboardingSetup_${companyKey}_completedAt`);
                  setOnboardingSetupTick(t => t + 1);
                };
                return (
                  <>
                    <h3 className="text-sm font-medium text-gray-700">Onboarding Setup</h3>
                    <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                            <p className="font-medium text-gray-900">Complete onboarding setup for this company</p>
                          </div>
                          <p className="text-xs text-green-600 mt-1 ml-7">Completed {new Date(completedAt).toLocaleDateString()}</p>
                        </div>
                        <button onClick={markPending} className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700">Mark Pending</button>
                      </div>
                    </div>
                  </>
                );
              })()}

              {/* Completed Monthly Tasks */}
              {(() => {
                const completedMonths = monthlyTasks.filter(month => monthlyTaskStatus[month.value]);

                if (completedMonths.length > 0) {
                  return (
                    <>
                      <h3 className="text-sm font-medium text-gray-700">Monthly Tasks</h3>
                      {completedMonths.map((month) => {
                        const txStatus = monthlyTransactionStatus[month.value];
                        const hasTransactions = txStatus?.hasTransactions;
                        return (
                          <div
                            key={`completed-monthly-${month.value}`}
                            className={`rounded-lg p-4 ${hasTransactions ? 'border border-green-200 bg-green-50' : 'border border-gray-300 bg-gray-100 opacity-60'}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${hasTransactions ? 'bg-green-500' : 'bg-gray-400'}`}>
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </span>
                                  <p className="font-medium text-gray-900">Upload Bank Statements: {month.label}</p>
                                </div>
                                <p className={`text-sm mt-1 ml-7 ${hasTransactions ? 'text-green-600' : 'text-gray-500'}`}>
                                  {hasTransactions ? 'All tasks complete' : 'Marked complete (no transactions)'}
                                </p>
                              </div>
                              <button
                                onClick={() => handleMarkMonthIncomplete(month.value)}
                                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                              >
                                Undo
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                }
                return null;
              })()}

              {/* Completed PMS Statement Tasks */}
              {(() => {
                const completedPmsMonths = monthlyTasks.filter(month => {
                  const txStatus = monthlyTransactionStatus[month.value];
                  return txStatus?.allConfirmed && pmsTransfersConfirmed[month.value];
                });

                if (completedPmsMonths.length > 0) {
                  return (
                    <>
                      <h3 className="text-sm font-medium text-gray-700 mt-4">PMS Transfer Tasks</h3>
                      {completedPmsMonths.map((month) => (
                        <div
                          key={`completed-pms-${month.value}`}
                          className="border border-green-200 bg-green-50 rounded-lg p-4"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </span>
                                <p className="font-medium text-gray-900">Confirm PMS Transfer: {month.label}</p>
                              </div>
                              <p className="text-sm text-green-600 mt-1 ml-7">PMS statement confirmed</p>
                            </div>
                            <button
                              onClick={() => handleRevertPmsTransfer(month.value)}
                              className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                            >
                              Undo
                            </button>
                          </div>
                        </div>
                      ))}
                    </>
                  );
                }
                return null;
              })()}

              {/* Completed Formation Tasks - hidden for skip-onboarding companies */}
              {!(companyData?.skipOnboarding === 'true' || companyData?.skipOnboarding === true) && (() => {
                const allTasks = adminTasks.tasks || adminTasks || [];
                const completedTasks = Array.isArray(allTasks)
                  ? allTasks.filter(t => t.status === 'completed')
                  : [];

                if (completedTasks.length > 0) {
                  return (
                    <>
                      <h3 className="text-sm font-medium text-gray-700 mt-4">Formation Tasks</h3>
                      {completedTasks.map((task) => (
                        <div
                          key={task.id}
                          className="border border-green-200 bg-green-50 rounded-lg p-4"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </span>
                                <p className="font-medium text-gray-900">{task.taskName}</p>
                              </div>
                              <p className="text-sm text-gray-600 mt-1 ml-7">{task.taskDescription}</p>
                              <p className="text-xs text-gray-500 mt-2 ml-7">
                                Company: {task.companyName}
                              </p>
                              {task.completedAt && (
                                <p className="text-xs text-green-600 mt-1 ml-7">
                                  Completed {new Date(task.completedAt).toLocaleDateString()}
                                  {task.completedBy && ` by ${task.completedBy}`}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => updateTaskStatus(task.id, 'pending')}
                              className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                            >
                              Mark Pending
                            </button>
                          </div>
                        </div>
                      ))}
                    </>
                  );
                }
                return null;
              })()}

              {/* Completed Default (Annual) Tasks */}
              {(() => {
                const defaultTasks = customTasks.filter(t => t.status === 'active' && t.taskType !== 'special' && t.isDefault === 'true');
                const currentYear = new Date().getFullYear().toString();
                const completedDefaults = defaultTasks.filter(task => {
                  const taskCompletions = customTaskCompletions.filter(c => c.taskId === task.id && c.status !== 'reverted');
                  return taskCompletions.some(c => c.periodKey?.startsWith(currentYear));
                });

                if (completedDefaults.length > 0) {
                  return (
                    <>
                      <h3 className="text-sm font-medium text-gray-700 mt-4">Annual Tasks</h3>
                      {completedDefaults.map(task => {
                        const taskCompletions = customTaskCompletions.filter(c => c.taskId === task.id);
                        const latestCompletion = taskCompletions[taskCompletions.length - 1];
                        return (
                          <div
                            key={`completed-default-${task.id}`}
                            className="border border-green-200 bg-green-50 rounded-lg p-4"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </span>
                                  <p className="font-medium text-gray-900">{task.title} - {currentYear}</p>
                                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">Yearly</span>
                                  {formatDueDate(task) && (
                                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">{formatDueDate(task)}</span>
                                  )}
                                </div>
                                {task.description && (
                                  <p className="text-sm text-gray-600 mt-1 ml-7">{task.description}</p>
                                )}
                                {latestCompletion && (
                                  <p className="text-xs text-green-600 mt-1 ml-7">
                                    Completed {new Date(latestCompletion.completedAt).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={() => handleUncompleteCustomTask(task.id, latestCompletion?.periodKey || new Date().toISOString().slice(0, 7))}
                                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                              >
                                Mark Pending
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                }
                return null;
              })()}

              {/* Completed Custom Tasks */}
              {(() => {
                const displayableTasks = customTasks.filter(t => t.status === 'active' && t.taskType !== 'special' && t.isDefault !== 'true');
                const currentPeriod = new Date().toISOString().slice(0, 7);

                const isCustomTaskCompleted = (task) => {
                  const taskCompletions = customTaskCompletions.filter(c => c.taskId === task.id);
                  if (task.taskType === 'file-upload') {
                    let uploadItems = [];
                    try {
                      if (task.fileUploadItems) {
                        uploadItems = typeof task.fileUploadItems === 'string'
                          ? JSON.parse(task.fileUploadItems)
                          : task.fileUploadItems;
                      }
                    } catch (e) { /* ignore */ }
                    if (uploadItems.length > 0) {
                      return uploadItems.every((_, idx) =>
                        taskCompletions.some(c => c.periodKey === currentPeriod && String(c.uploadItemIndex) === String(idx))
                      );
                    }
                    return taskCompletions.some(c => c.periodKey === currentPeriod);
                  }
                  return taskCompletions.some(c => c.periodKey === currentPeriod);
                };

                const completedCustom = displayableTasks.filter(task => isCustomTaskCompleted(task));

                if (completedCustom.length > 0) {
                  return (
                    <>
                      <h3 className="text-sm font-medium text-gray-700 mt-4">Custom Tasks</h3>
                      {completedCustom.map(task => {
                        const taskCompletions = customTaskCompletions.filter(c => c.taskId === task.id);
                        const latestCompletion = taskCompletions[taskCompletions.length - 1];
                        return (
                          <div
                            key={`completed-custom-${task.id}`}
                            className="border border-green-200 bg-green-50 rounded-lg p-4"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </span>
                                  <p className="font-medium text-gray-900">
                                    {task.title}
                                    {task.isRepeated === 'true' && (() => {
                                      const now = new Date();
                                      if (task.cycle === 'yearly') {
                                        return ` - ${now.getFullYear()}`;
                                      } else {
                                        return ` - ${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()}`;
                                      }
                                    })()}
                                  </p>
                                  {task.isRepeated === 'true' && (
                                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                      {task.cycle === 'yearly' ? 'Yearly' : 'Monthly'}
                                    </span>
                                  )}
                                  {formatDueDate(task) && (
                                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">{formatDueDate(task)}</span>
                                  )}
                                </div>
                                {task.description && (
                                  <p className="text-sm text-gray-600 mt-1 ml-7">{task.description}</p>
                                )}
                                {latestCompletion && (
                                  <p className="text-xs text-green-600 mt-1 ml-7">
                                    Completed {new Date(latestCompletion.completedAt).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={() => handleUncompleteCustomTask(task.id, latestCompletion?.periodKey || new Date().toISOString().slice(0, 7))}
                                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                              >
                                Mark Pending
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                }
                return null;
              })()}

              {/* No completed tasks message */}
              {(() => {
                const allTasks = adminTasks.tasks || adminTasks || [];
                const formationCompletedCount = Array.isArray(allTasks)
                  ? allTasks.filter(t => t.status === 'completed').length
                  : 0;
                const monthlyCompletedCount = monthlyTasks.filter(month => monthlyTaskStatus[month.value]).length;
                const pmsCompletedCount = monthlyTasks.filter(month => {
                  const txStatus = monthlyTransactionStatus[month.value];
                  return txStatus?.allConfirmed && pmsTransfersConfirmed[month.value];
                }).length;

                const currentYear = new Date().getFullYear().toString();
                const currentPeriod = new Date().toISOString().slice(0, 7);
                const defaultTasks = customTasks.filter(t => t.status === 'active' && t.taskType !== 'special' && t.isDefault === 'true');
                const defaultCompletedCount = defaultTasks.filter(task => {
                  const tc = customTaskCompletions.filter(c => c.taskId === task.id && c.status !== 'reverted');
                  return tc.some(c => c.periodKey?.startsWith(currentYear));
                }).length;
                const displayableTasks = customTasks.filter(t => t.status === 'active' && t.taskType !== 'special' && t.isDefault !== 'true');
                const customCompletedCount = displayableTasks.filter(task => {
                  const tc = customTaskCompletions.filter(c => c.taskId === task.id && c.status !== 'reverted');
                  if (task.taskType === 'file-upload') {
                    let uploadItems = [];
                    try {
                      if (task.fileUploadItems) {
                        uploadItems = typeof task.fileUploadItems === 'string'
                          ? JSON.parse(task.fileUploadItems) : task.fileUploadItems;
                      }
                    } catch (e) { /* ignore */ }
                    if (uploadItems.length > 0) {
                      return uploadItems.every((_, idx) =>
                        tc.some(c => c.periodKey === currentPeriod && String(c.uploadItemIndex) === String(idx))
                      );
                    }
                  }
                  return tc.some(c => c.periodKey === currentPeriod);
                }).length;

                if (formationCompletedCount === 0 && monthlyCompletedCount === 0 && pmsCompletedCount === 0 && defaultCompletedCount === 0 && customCompletedCount === 0) {
                  return <p className="text-gray-500 text-center py-4">No completed tasks yet</p>;
                }
                return null;
              })()}
            </div>
          )}
        </div>

        {/* Submission Details - hidden for skip-onboarding companies and once formation is complete */}
        {selectedSubmission && !(companyData?.skipOnboarding === 'true' || companyData?.skipOnboarding === true) && !isFormationComplete() && (
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Submission Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Company Information</h3>
                <div className="space-y-1">
                  <p><span className="text-gray-500">Name:</span> {selectedSubmission['Company Name']}</p>
                  <p><span className="text-gray-500">Status:</span> {selectedSubmission.companyAlreadyFormed ? 'Already Formed' : 'New Formation'}</p>
                  <p><span className="text-gray-500">Submitted:</span> {new Date(selectedSubmission['Submitted At']).toLocaleDateString()}</p>
                  <p><span className="text-gray-500">By:</span> {selectedSubmission['Submitted By']}</p>
                  <p className="flex items-center">
                    <span className="text-gray-500">Formation Form:</span>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                      selectedSubmission['Status'] === 'submitted'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedSubmission['Status'] === 'submitted' ? 'Completed' : 'Draft'}
                    </span>
                  </p>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                    <p className="flex-1">
                      <span className="text-gray-500">Email:</span> {selectedSubmission['Company Email'] || 'Not set'}
                    </p>
                    <button
                      onClick={openEmailChangeModal}
                      className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors duration-200"
                      title="Change company email"
                    >
                      Change
                    </button>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Founders Status</h3>
                <div className="space-y-2">
                  {getSubmissionFounders(selectedSubmission).map((founder, index) => (
                    <div key={index} className="flex items-center justify-between text-sm border border-gray-200 rounded p-2">
                      <div>
                        <p className="font-medium">{founder.fullName || `Founder ${index + 1}`}</p>
                        <p className="text-gray-500 text-xs">{founder.email}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        founder.isInvited && (!founder.fullName || !founder.cellPhone)
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {founder.isInvited && (!founder.fullName || !founder.cellPhone) ? 'Pending' : 'Complete'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Progress Summary</h3>
                <div className="space-y-1">
                  {(() => {
                    const tasks = getTasksBySubmission(selectedSubmission.ID);
                    const completed = tasks.filter(t => t.status === 'completed').length;
                    const total = tasks.length;
                    
                    return (
                      <>
                        <p><span className="text-gray-500">Tasks:</span> {completed}/{total} completed</p>
                        <p><span className="text-gray-500">Progress:</span> {getCompletionPercentage(tasks)}%</p>
                        <p><span className="text-gray-500">Status:</span> 
                          <span className={`ml-1 px-2 py-1 text-xs rounded ${
                            completed === total && total > 0 ? 'bg-green-100 text-green-800' :
                            completed > 0 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {completed === total && total > 0 ? 'Complete' :
                             completed > 0 ? 'In Progress' : 'Pending'}
                          </span>
                        </p>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Admin Edit Access */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => navigate('/company-formation', {
                  state: {
                    adminEdit: true,
                    submissionId: selectedSubmission.ID
                  }
                })}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>Edit Company Formation</span>
              </button>
            </div>
          </div>
        )}

        {/* Bank Information - hidden once formation is complete (info lives on Company Info page) */}
        {selectedSubmission && !isFormationComplete() && (
          <div className="card p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Bank Information</h2>
              <button
                onClick={fetchBankInfo}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                Refresh Bank Info
              </button>
            </div>

            {(() => {
              const submissionBankInfo = bankInfo.filter(
                info => info["Company Name"] === userInfo?.selectedCompany
              );

              if (submissionBankInfo.length === 0) {
                return (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-2">
                      <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <p className="text-gray-500">No bank information submitted yet</p>
                    <p className="text-sm text-gray-400 mt-1">Founders will submit bank details after Gmail setup is completed</p>
                  </div>
                );
              }

              return (
                <div className="space-y-4">
                  {submissionBankInfo.map((info, index) => (
                    <div key={info.id || index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-lg font-medium text-gray-900">Bank Account Information</h3>
                        <span className="text-sm text-gray-500">
                          Submitted {new Date(info["Submitted At"]).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Bank Name</p>
                          <p className="text-gray-900">{info['Bank Name'] || 'Mercury Bank'}</p>
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">Submitted By</p>
                          <p className="text-gray-900">{info["Submitted By"]}</p>
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">Routing Number</p>
                          <p className="text-gray-900 font-mono">{info["Routing Number"]}</p>
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">Account Number</p>
                          <p className="text-gray-900 font-mono">
                            {info['Account Number'] ? info["Routing Number"] : 'Not provided'}
                          </p>
                        </div>

                        {info.bankAddress && (
                          <div className="md:col-span-2">
                            <p className="text-sm font-medium text-gray-700">Bank Address</p>
                            <p className="text-gray-900">{info['Bank Address']}</p>
                          </div>
                        )}

                        {info.bankPhone && (
                          <div>
                            <p className="text-sm font-medium text-gray-700">Bank Phone</p>
                            <p className="text-gray-900">{info['Bank Phone']}</p>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="flex items-center text-sm text-gray-600">
                          <svg className="w-4 h-4 mr-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Bank information submitted and ready for review
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Email Change Modal */}
        {showEmailChangeModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Change Company Email</h3>
                  <button
                    onClick={() => {
                      setShowEmailChangeModal(false);
                      setNewCompanyEmail('');
                    }}
                    disabled={emailChangeLoading}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <p className="text-gray-900 font-medium">{selectedSubmission?.['Company Name']}</p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Email
                  </label>
                  <p className="text-gray-600">{selectedSubmission?.['Company Email'] || 'Not set'}</p>
                </div>

                <div className="mb-4">
                  <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    New Email Address
                  </label>
                  <input
                    id="newEmail"
                    type="email"
                    value={newCompanyEmail}
                    onChange={(e) => setNewCompanyEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter new email address"
                    disabled={emailChangeLoading}
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Warning:</strong> Changing the company email will update all associated records. This action cannot be undone.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowEmailChangeModal(false);
                      setNewCompanyEmail('');
                    }}
                    disabled={emailChangeLoading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEmailChange}
                    disabled={emailChangeLoading || !newCompanyEmail || newCompanyEmail === selectedSubmission?.['Company Email']}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {emailChangeLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating...
                      </>
                    ) : (
                      'Update Email'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Confirmation Modal */}
        <EmailConfirmationModal
          isOpen={emailConfirmation.isOpen}
          onClose={() => setEmailConfirmation({ isOpen: false, emailData: null, onConfirm: null })}
          onConfirm={emailConfirmation.onConfirm}
          emailData={emailConfirmation.emailData}
          loading={false}
        />

        {/* Gmail Original Email Preview Modal (First Modal) */}
        <EmailPreviewEditModal
          isOpen={showGmailOriginalEmailPreview}
          onClose={() => {
            setShowGmailOriginalEmailPreview(false);
            setGmailOriginalEmailData({});
          }}
          onSend={handleSendGmailOriginalEmail}
          templateKey="email_verification_original"
          variables={{
            companyName: selectedSubmission?.['Company Name'] || '',
            emailUpdates: gmailOriginalEmailData.emailUpdates || []
          }}
          recipientEmail={gmailOriginalEmailData.emailUpdates?.map(u => u.oldEmail).join(', ') || ''}
          title="Gmail Email Update - Verification Email to Original Addresses"
          loading={sendingGmailOriginalEmail}
          showEditToggle={true}
          allowTemplateUpdate={false}
        />

        {/* Gmail Company Email Preview Modal (Second Modal) */}
        <EmailPreviewEditModal
          isOpen={showGmailBulkEmailPreview}
          onClose={() => {
            setShowGmailBulkEmailPreview(false);
            setGmailBulkEmailData({});
          }}
          onSend={handleSendGmailBulkEmail}
          templateKey="email_verification_company"
          variables={{
            companyName: selectedSubmission?.['Company Name'] || '',
            emailUpdates: gmailBulkEmailData.emailUpdates || []
          }}
          recipientEmail={gmailBulkEmailData.emailUpdates?.map(u => u.newEmail).join(', ') || ''}
          title="Gmail Email Update - Verification Email to Company Addresses"
          loading={sendingGmailBulkEmail}
          showEditToggle={true}
          allowTemplateUpdate={false}
        />

        {/* EIN Email Preview Modal */}
        <EmailPreviewEditModal
          isOpen={showEINEmailPreview}
          onClose={() => {
            setShowEINEmailPreview(false);
            setEinTaskId(null);
          }}
          onSend={handleSendEINEmail}
          templateKey="ein_obtained"
          variables={einEmailVariables}
          recipientEmail={getSubmissionFounderEmails(selectedSubmission)}
          title="EIN Obtained - Bank Account Setup Email"
          loading={sendingEINEmail}
          showEditToggle={true}
          allowTemplateUpdate={true}
        />

        {/* Board Minutes Email Preview Modal */}
        <EmailPreviewEditModal
          isOpen={showBoardMinutesEmailPreview}
          onClose={() => {
            setShowBoardMinutesEmailPreview(false);
            setBoardMinutesEmailData({});
            setBoardMinutesTaskId(null);
          }}
          onSend={handleSendBoardMinutesEmail}
          templateKey="board_minutes_required"
          variables={boardMinutesEmailData}
          recipientEmail={getSubmissionFounderEmails(selectedSubmission)}
          title="Board Meeting Minutes Signature Required"
          loading={sendingBoardMinutesEmail}
          showEditToggle={true}
          allowTemplateUpdate={true}
        />

        {/* CAGE Complete Email Preview Modal */}
        <EmailPreviewEditModal
          isOpen={showCAGEEmailPreview}
          onClose={() => {
            setShowCAGEEmailPreview(false);
            setCageTaskId(null);
          }}
          onSend={handleSendCAGEEmail}
          templateKey="cage_complete"
          variables={cageEmailVariables}
          recipientEmail={getSubmissionFounderEmails(selectedSubmission)}
          title="CAGE Registration Complete"
          loading={sendingCAGEEmail}
          showEditToggle={true}
          allowTemplateUpdate={true}
        />

        {/* eRA Registration Email Preview Modal */}
        <EmailPreviewEditModal
          isOpen={showERAEmailPreview}
          onClose={() => {
            setShowERAEmailPreview(false);
            setEraTaskId(null);
          }}
          onSend={handleSendERAEmail}
          templateKey="era_registration_complete"
          variables={eraEmailVariables}
          recipientEmail={getSubmissionFounderEmails(selectedSubmission)}
          title="eRA Commons Registration Complete"
          loading={sendingERAEmail}
          showEditToggle={true}
          allowTemplateUpdate={true}
        />

        {/* SBA Registration Notification Email Preview Modal */}
        <EmailPreviewEditModal
          isOpen={showSBANotificationPreview}
          onClose={() => {
            setShowSBANotificationPreview(false);
            setSoFsrTaskId(null);
          }}
          onSend={handleSendSBANotification}
          templateKey="sba_registration_required"
          variables={sbaEmailVariables}
          recipientEmail={getSubmissionFounderEmails(selectedSubmission)}
          title="SBA Registration Required - Action Needed"
          loading={sendingSBANotification}
          showEditToggle={true}
          allowTemplateUpdate={true}
        />

        {/* AOR Assignment Notification Email Preview Modal */}
        <EmailPreviewEditModal
          isOpen={showAORNotificationPreview}
          onClose={() => {
            setShowAORNotificationPreview(false);
            setAorNotificationTaskId(null);
          }}
          onSend={handleSendAORNotification}
          templateKey="aor_assignment_required"
          variables={aorEmailVariables}
          recipientEmail={getSubmissionFounderEmails(selectedSubmission)}
          title="Add StreamLaunch as AOR in Grants.gov"
          loading={sendingAORNotification}
          showEditToggle={true}
          allowTemplateUpdate={true}
        />

        {/* AOR Confirmation Complete Email Preview Modal */}
        <EmailPreviewEditModal
          isOpen={showAORConfirmationEmailPreview}
          onClose={() => {
            setShowAORConfirmationEmailPreview(false);
            setAorConfirmTaskId(null);
          }}
          onSend={handleSendAORConfirmationEmail}
          templateKey="sbir_preparation_complete"
          variables={aorConfirmEmailVariables}
          recipientEmail={getSubmissionFounderEmails(selectedSubmission)}
          title="SBIR Preparation Complete"
          loading={sendingAORConfirmationEmail}
          showEditToggle={true}
          allowTemplateUpdate={true}
        />

        {/* Monthly Cycle Settings Modal */}
        {showMonthlyCycleModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Monthly Tasks Cycle Settings</h3>
                  <button
                    onClick={() => setShowMonthlyCycleModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-6">
                <p className="text-gray-600 text-sm mb-4">
                  Set the start month for the monthly tasks cycle. After this date, new tasks will be automatically generated for each month.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <select
                      value={selectedStartYear}
                      onChange={(e) => setSelectedStartYear(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {availableYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                    <select
                      value={selectedStartMonth}
                      onChange={(e) => setSelectedStartMonth(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {monthNames.map(month => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowMonthlyCycleModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveMonthlyCycleSettings}
                    disabled={savingCycleSettings}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {savingCycleSettings ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTodolist;